/**
 * Shared helpers for session forms.
 * Builds create/edit DTOs and hydrates group composition.
 * Validates participants and routes tutoring drag-and-drop.
 */
import * as React from 'react';
import type {
  AbsenceReason,
  AttendanceStatus,
  ParticipantAttendance,
  ParticipantAttendanceById,
} from '@/types/attendance.types';
import {
  checkStudentAvailability,
  checkTutorAvailability,
  pollSessionsBulkJob,
} from '@/services/inperson/sessions.service';
import {
  addSessionParticipant,
  deleteSessionParticipant,
} from '@/services/remote/sessions.service';
import { toast } from '@/components/ui/toast';
import {
  pointerWithin,
  closestCenter,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import { toCalendarDateString, toLocalDateString } from '@/utils/format-date-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel, roleKind } from '@/utils/role-label-utils';
import type { CreateActivityDto } from '@/types/activity.types';
import type { CreateActivityTypeDto } from '@/types/activity-type.types';
import {
  COLLECTIVE_SESSION_TYPE_VALUES,
  isGroupSessionType,
  type CreateSessionDto,
  type PlanificationType,
  type RecurrenceType,
  type RecurrenceUpdateScope,
  type ClassroomCapacityResult,
  type SessionFormat,
  type SessionMode,
  type SessionParticipant,
  type SessionType,
  type SessionWithParticipants,
  type UpdateSessionDto,
  type CancelSessionReasonCode,
  type SessionGroup,
  type SessionGroupChild,
  type UpdateSessionGroupChildDto,
  type UpdateSessionGroupDto,
  type SessionFormData,
  type SessionSlot,
  type SessionStatus,
  type QueuedSessionsBulkJob,
  type SessionBulkJob,
  type SessionsBulkTrackOptions,
} from '@/types/inperson/session.types';
import type { Establishment } from '@/types/establishment.types';
import type { ProposedPairSource } from '@/types/inperson/matching.types';
import {
  formatEstablishmentSelectLabel,
  stubEstablishmentForSelect,
} from '@/utils/format-establishment-select-label-utils';
import {
  INDIVIDUAL_SESSION_TYPE_VALUES,
  INFORMATION_MEETING_MAX_PARTICIPANTS,
  SESSION_IN_PERSON_TYPE_OPTIONS,
  STUDENT_ABSENCE_REASONS,
  TUTOR_ABSENCE_REASONS,
  type SessionInPersonCreateTypeOption,
} from '@/components/sessions/session-configs';
import { isTerminalStatus } from '@/utils/common-utils';
import type { SelectedUser, UserWithProfile } from '@/types/user.types';
import { isVscObserverUser, isVolunteerUser } from '@/types/user.types';
import type { SlotAvailabilityById, SlotAvailabilityEntry } from '@/types/slot-availability.types';

export { isTerminalStatus } from '@/utils/common-utils';

/** Participant id fields filled later by pairing builders. */
type CreateSessionParticipantField = 'tutorIds' | 'studentIds' | 'volunteerIds' | 'pairs';
/** Create payload minus participant fields filled later by pairing builders. */
type CreateSessionBaseDto = Omit<CreateSessionDto, CreateSessionParticipantField>;

/** Value/label pair used by session form selects. */
type FormSelectOption = { value: string; label: string };

/** Result of a pre-add validation / availability gate. */
export type AddParticipantOutcome =
  | { ok: false; kind: 'warning' | 'info' | 'error'; message: string }
  | { ok: true; kind?: 'success'; message?: string };

/** Inputs for {@link useSessionTutorAvailability}. */
export interface UseSessionTutorAvailabilityOptions {
  tutors: UserWithProfile[];
  slot: SessionSlot;
  excludeSessionId?: string;
  excludeSessionGroupId?: string;
  enabled?: boolean;
}

/** Inputs for {@link useSessionStudentAvailability}. */
export interface UseSessionStudentAvailabilityOptions {
  students: UserWithProfile[];
  slot: SessionSlot;
  excludeSessionId?: string;
  excludeSessionGroupId?: string;
  enabled?: boolean;
}

/** Availability-check callback used before adding a dragged participant. */
type EnsureParticipantFn = (
  userData: UserWithProfile,
  onSyncChecksPassed?: () => void,
  successMessage?: string,
) => Promise<boolean>;

/** Callbacks and selection used by the tutoring drop handler. */
export type TutoringDragEndDeps = {
  isGroupMode: boolean;
  selectedStudents: UserWithProfile[];
  selectedTutors: UserWithProfile[];
  matchStudentToTutor: (student: UserWithProfile, tutorId: string) => void;
  addTutorToSession: (user: UserWithProfile) => void;
  addStudentToSession: (user: UserWithProfile) => void;
  ensureTutorAvailable: EnsureParticipantFn;
  ensureStudentAvailable: EnsureParticipantFn;
  handleRemoveTutor: (userId: string) => void;
  handleRemoveStudent: (userId: string) => void;
};

/** Drop result: hint when a student landed unassigned in group mode. */
export type TutoringDragEndOutcome = { assignStudentToGroupHint?: boolean };

/** Outcome of a submit-time validation check. */
type ValidateResult = { valid: boolean; message?: string };

/** Submit-time availability validator used by tutoring create/edit. */
type SubmitAvailability = {
  validateForSubmit: () => Promise<{ ok: true } | { ok: false; unavailable: UserWithProfile[] }>;
};

/** Full create/edit submit bag: validation, mutations, and pairing state. */
export type TutoringSubmitCtx = {
  mode: 'create' | 'edit';
  queuedJob: unknown;
  createPending: boolean;
  validation: { validateForm: (data: SessionFormData) => boolean };
  sessionData: SessionFormData;
  validateActivityForms: () => boolean;
  showParticipants: boolean;
  isGroupMode: boolean;
  selectedTutors: UserWithProfile[];
  selectedStudents: UserWithProfile[];
  studentToTutor: Record<string, string>;
  tutorAvailability: SubmitAvailability;
  studentAvailability: SubmitAvailability;
  setIsCheckingSubmit: (value: boolean) => void;
  buildActivityData: () => {
    activityId?: string;
    newActivity?: CreateActivityDto;
    newActivityType?: CreateActivityTypeDto;
  };
  getSelectedEntities: () => {
    establishment?: { name?: string };
    program?: { name?: string };
    activity?: { label?: string };
    classroom?: { name?: string; label?: string };
  };
  selectedEstablishmentDetail?: { name?: string } | null;
  isEditGroup: boolean;
  isRecurringSeries: boolean;
  recurrenceUpdateScope: RecurrenceUpdateScope;
  tutorToChildId: Record<string, string>;
  soleChildId: string | null;
  createMutation: { mutate: (input: { dtos: CreateSessionDto[]; isGroupMode: boolean }) => void };
  updateGroupMutation: { mutate: (payload: UpdateSessionGroupDto) => void };
  updateMutation: { mutate: (dto: UpdateSessionDto) => void };
  formData: {
    establishments: Array<{ id: string; name?: string }>;
    programs: Array<{ id: string; name?: string }>;
    activities: Array<{ id: string; label?: string }>;
    classrooms: Array<{ id: string; name?: string; label?: string }>;
  };
  session?: { recurrenceGroupId?: string | null; mode?: string } | null;
};

/** Discriminated create-error: capacity, timeout, or generic message. */
export type CreateSessionMutationErrorOutcome =
  | { kind: 'capacity'; result: ClassroomCapacityResult }
  | { kind: 'timeout' }
  | { kind: 'error'; message: string };

/** Why an add was refused: missing id, cap, or already selected. */
export type AddParticipantBlock = 'missing-id' | 'max' | 'already' | null;

/** Toast payload after a blocked add. */
export type AddParticipantToast = { kind: 'error' | 'warning' | 'info'; text: string };

/** Narrows the type dropdown by planification (individual / collective / all). */
export function getSessionTypeOptions(planificationType?: PlanificationType) {
  if (planificationType === 'individual') {
    return SESSION_IN_PERSON_TYPE_OPTIONS.filter((opt) =>
      INDIVIDUAL_SESSION_TYPE_VALUES.includes(opt.value as SessionType),
    );
  }
  if (planificationType === 'collective') {
    return SESSION_IN_PERSON_TYPE_OPTIONS.filter((opt) =>
      COLLECTIVE_SESSION_TYPE_VALUES.includes(opt.value as SessionType),
    );
  }
  return SESSION_IN_PERSON_TYPE_OPTIONS;
}

/**
 * Weekday (0=Sunday .. 6=Saturday) for a session date `YYYY-MM-DD`.
 * Parsed locally to avoid UTC shift. Independent of session time.
 */
export function getSessionDayOfWeek(
  sessionDate: string | Date | undefined | null,
): number | undefined {
  if (sessionDate instanceof Date) {
    if (Number.isNaN(sessionDate.getTime())) return undefined;
    return sessionDate.getDay();
  }
  if (typeof sessionDate !== 'string' || !sessionDate.trim()) return undefined;
  const ymd = sessionDate.trim().slice(0, 10);
  const [y, m, d] = ymd.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return undefined;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.getDay();
}

/** Blank form seed; format defaults to in_person, recurrence to none. */
export const EMPTY_SESSION_DATA: SessionFormData = {
  establishmentId: '',
  schoolYearId: '',
  roomId: '',
  programId: '',
  levelIds: [],
  subjectIds: [],
  classId: '',
  specialtyIds: [],
  activityId: '',
  sessionDate: '',
  startTime: '',
  endTime: '',
  type: '',
  sessionFormat: 'in_person',
  mode: '',
  recurrenceType: 'none',
  recurrenceRule: null,
  topic: '',
  joinLink: '',
  planDuringHolidays: true,
  internalNotes: '',
};

/** Blank inline-activity create draft. */
export const EMPTY_ACTIVITY_DRAFT: Partial<CreateActivityDto> = {
  code: '',
  label: '',
  description: '',
  status: 'active',
};

/** Blank inline-activity-type create draft. */
export const EMPTY_ACTIVITY_TYPE_DRAFT: Partial<CreateActivityTypeDto> = {
  code: '',
  label: '',
  description: '',
  status: 'active',
};

/** Thrown when the classroom cannot fit the requested participants. */
export class ClassroomCapacityExceededError extends Error {
  constructor(public readonly result: ClassroomCapacityResult) {
    super(result.message ?? getErrorMessage('classroomCapacityExceeded'));
    this.name = 'ClassroomCapacityExceededError';
  }
}

/** Coerces unknown attendance strings to a known status, defaulting to present. */
export function normalizeAttendanceStatus(value: string | undefined | null): AttendanceStatus {
  if (value === 'present' || value === 'absent' || value === 'excused' || value === 'pending') {
    return value;
  }
  return 'present';
}

/** Rolls HH:MM forward by one hour, wrapping at midnight. */
export function addOneHour(time: string): string {
  const [hours, minutes] = time.split(':');
  const newHour = (Number.parseInt(hours, 10) + 1) % 24;
  return `${newHour.toString().padStart(2, '0')}:${minutes}`;
}

/** Full name, then email, then id as last-resort picker label. */
function formatSelectedUserLabel(user: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string {
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || user.id;
}

/** Maps a user profile onto a picker {id, label} row. */
export function toSelectedUser(user: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): SelectedUser {
  return { id: user.id, label: formatSelectedUserLabel(user) };
}

/** True when the user is a VSC observer treated as a floating tutor. */
function isVscTutor(user: UserWithProfile): boolean {
  return isVscObserverUser(user);
}

/** Maps volunteer / VSC observers to volunteer; everyone else is tutor. */
export function facilitatorRoleForSession(user: UserWithProfile): 'tutor' | 'volunteer' {
  if (isVolunteerUser(user) || isVscObserverUser(user) || user.currentRole === 'volunteer') {
    return 'volunteer';
  }
  return 'tutor';
}

/** True when the facilitator should be sent in volunteerIds, not tutorIds. */
function isVolunteerFacilitator(user: UserWithProfile): boolean {
  return facilitatorRoleForSession(user) === 'volunteer';
}

/** Cartesian student×tutor pairs for classic (non-group) mode. */
function buildSessionPairs(
  tutorIds: string[],
  studentIds: string[],
): Array<{ studentUserId: string; tutorUserId: string }> {
  const pairs: Array<{ studentUserId: string; tutorUserId: string }> = [];
  for (const studentUserId of studentIds) {
    for (const tutorUserId of tutorIds) {
      pairs.push({ studentUserId, tutorUserId });
    }
  }
  return pairs;
}

/** Caps individual tutoring at 1; otherwise uses the activity max. */
function getMaxTutorsForSessionType(
  type: SessionType | '',
  activityMaxTutors: number | null | undefined,
): number | null {
  if (type === 'individual_tutoring') {
    return 1;
  }
  return activityMaxTutors ?? null;
}

/** User-facing cap message when adding a tutor in edit/type-aware mode. */
export function tutorLimitMessageForSessionType(
  type: SessionType | '',
  maxTutors: number | null,
  _incomingName?: string,
): string {
  if (type === 'individual_tutoring') {
    return getErrorMessage('individualTutoringSingleTutor');
  }
  return getErrorMessage({ code: 'maxTutorsCapacityReached', max: maxTutors ?? 0 });
}

/** Flattens pair rows into a studentId → tutorId map (last pair wins). */
function studentToTutorFromPairs(
  pairs: Array<{ studentUserId: string; tutorUserId: string }>,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const p of pairs) {
    map[p.studentUserId] = p.tutorUserId;
  }
  return map;
}

/** VSC tutors not assigned as a pair owner — attached as extra volunteers. */
function getFloatingVscTutorIds(
  tutors: UserWithProfile[],
  studentToTutor: Record<string, string>,
): string[] {
  return tutors
    .filter((t) => isVscTutor(t) && !Object.values(studentToTutor).includes(t.id))
    .map((t) => t.id);
}

/** Facilitators that own a child session (non-VSC, or VSC already paired). */
export function getGroupSessionOwnerTutors(
  tutors: UserWithProfile[],
  studentToTutor: Record<string, string>,
): UserWithProfile[] {
  return tutors.filter((t) => {
    if (!isVscTutor(t)) return true;
    return Object.values(studentToTutor).includes(t.id);
  });
}

/** Requires at least one facilitator before save. */
export function validateMinParticipants(
  tutors: UserWithProfile[],
  _students: UserWithProfile[],
  _options?: { isGroupMode?: boolean; studentToTutor?: Record<string, string> },
): { valid: boolean; message?: string } {
  if (tutors.length < 1) {
    return {
      valid: false,
      message: getErrorMessage('minFacilitatorRequired'),
    };
  }
  return { valid: true };
}

/** Single-session tutor/student/volunteer ids plus cartesian pairs. */
export function buildClassicModeParticipantPayload(
  tutors: UserWithProfile[],
  students: UserWithProfile[],
): {
  tutorIds: string[];
  studentIds: string[];
  volunteerIds: string[];
  pairs: Array<{ studentUserId: string; tutorUserId: string }>;
} {
  const tutorIds = tutors.filter((t) => !isVolunteerFacilitator(t)).map((t) => t.id);
  const volunteerIds = tutors.filter((t) => isVolunteerFacilitator(t)).map((t) => t.id);
  const studentIds = students.map((s) => s.id);
  const pairingFacilitatorIds = tutorIds.length > 0 ? tutorIds : volunteerIds;
  const pairs = buildSessionPairs(pairingFacilitatorIds, studentIds);
  return { tutorIds, studentIds, volunteerIds, pairs };
}

/** Update payload: owners, floating VSC volunteers, and explicit pairs. */
export function buildGroupModeUpdateParticipantPayload(
  tutors: UserWithProfile[],
  students: UserWithProfile[],
  studentToTutor: Record<string, string>,
): {
  tutorIds: string[];
  studentIds: string[];
  volunteerIds: string[];
  pairs: Array<{ studentUserId: string; tutorUserId: string }>;
} {
  const owners = getGroupSessionOwnerTutors(tutors, studentToTutor);
  const tutorIds = owners.filter((t) => !isVolunteerFacilitator(t)).map((t) => t.id);
  const ownerVolunteerIds = owners.filter((t) => isVolunteerFacilitator(t)).map((t) => t.id);
  const studentIds = students.map((s) => s.id);
  const floatingVscIds = getFloatingVscTutorIds(tutors, studentToTutor);
  const volunteerIds = [...new Set([...ownerVolunteerIds, ...floatingVscIds])];
  const pairs = Object.entries(studentToTutor).map(([studentUserId, tutorUserId]) => ({
    studentUserId,
    tutorUserId,
  }));
  return { tutorIds, studentIds, volunteerIds, pairs };
}

/** One create DTO per owner, with floating VSC ids on every child. */
export function buildGroupModeSessionDtos(
  tutors: UserWithProfile[],
  studentToTutor: Record<string, string>,
  baseDto: CreateSessionBaseDto,
): CreateSessionDto[] {
  const floatingVscIds = getFloatingVscTutorIds(tutors, studentToTutor);
  const sessionOwners = getGroupSessionOwnerTutors(tutors, studentToTutor);

  return sessionOwners.map((owner) => {
    const studentIds = Object.entries(studentToTutor)
      .filter(([, tid]) => tid === owner.id)
      .map(([sid]) => sid);
    const isVolunteerOwner = isVolunteerFacilitator(owner);
    const tutorIds = isVolunteerOwner ? [] : [owner.id];
    const volunteerIds = [
      ...(isVolunteerOwner ? [owner.id] : []),
      ...floatingVscIds.filter((id) => id !== owner.id),
    ];
    return {
      ...baseDto,
      tutorIds,
      studentIds,
      volunteerIds,
      pairs: buildSessionPairs([owner.id], studentIds),
    };
  });
}

/** Requires at least one child DTO before group save. */
export function validateGroupSessionDtos(
  _tutors: UserWithProfile[],
  _studentToTutor: Record<string, string>,
  dtos: Array<{
    tutorIds?: string[];
    studentIds?: string[];
    volunteerIds?: string[];
    pairs?: Array<{ studentUserId: string; tutorUserId: string }>;
  }>,
): { valid: boolean; message?: string } {
  if (dtos.length < 1) {
    return {
      valid: false,
      message: getErrorMessage('minFacilitatorRequired'),
    };
  }
  return { valid: true };
}

/** Placeholder — currently always valid (orphans checked separately). */
export function validateGroupCreateTutorsHaveStudents(
  _tutors: UserWithProfile[],
  _studentToTutor: Record<string, string>,
): { valid: boolean; message?: string } {
  return { valid: true };
}

/** Blocks save when a student has no assigned facilitator. */
export function validateOrphanStudentsForGroupMode(
  students: UserWithProfile[],
  studentToTutor: Record<string, string>,
): { valid: boolean; message?: string } {
  const orphans = students.filter((s) => !studentToTutor[s.id]);
  if (orphans.length === 0) {
    return { valid: true };
  }
  return {
    valid: false,
    message: getErrorMessage('orphanStudentsMustAssign'),
  };
}

/** True when date, start, and end are all non-empty. */
export function isSessionSlotComplete(slot: SessionSlot): boolean {
  return !!(slot.sessionDate?.trim() && slot.startTime?.trim() && slot.endTime?.trim());
}

/** Tutor fallback label when first/last name are missing. */
export function formatTutorDisplayName(user: UserWithProfile): string {
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || getRoleLabel('tutor');
}

/** Hard-block copy when tutors have slot conflicts. */
export function buildSubmitBlockedMessage(unavailable: UserWithProfile[]): string {
  if (unavailable.length === 0) return '';
  const names = unavailable.map(formatTutorDisplayName).join(', ');
  if (unavailable.length === 1) {
    return getErrorMessage({ code: 'tutorUnavailableAtSlot', names });
  }
  return getErrorMessage({ code: 'tutorsUnavailableAtSlot', names });
}

/** Live hint while tutor availability is checking or conflicting. */
export function buildSoftHintMessage(conflictCount: number, isChecking: boolean): string | null {
  if (isChecking) return getErrorMessage('tutorAvailabilityChecking');
  if (conflictCount === 1) return getErrorMessage('tutorSlotConflictOne');
  if (conflictCount > 1) {
    return getErrorMessage({ code: 'tutorSlotConflictMany', count: conflictCount });
  }
  return null;
}

/** Probes one tutor slot; unknown on network failure. */
async function checkOneTutorSlotAvailability(
  tutorId: string,
  slot: SessionSlot,
  excludeSessionId?: string,
  excludeSessionGroupId?: string,
): Promise<SlotAvailabilityEntry> {
  try {
    const result = await checkTutorAvailability({
      tutorId,
      sessionDate: slot.sessionDate,
      startTime: slot.startTime,
      endTime: slot.endTime,
      excludeSessionId,
      excludeSessionGroupId,
    });
    return result.available
      ? { status: 'available' }
      : { status: 'conflict', conflictingSession: result.conflictingSession };
  } catch {
    return { status: 'unknown' };
  }
}

/** Parallel availability map for all selected tutors. */
async function checkTutorsSlotAvailability(
  tutors: UserWithProfile[],
  slot: SessionSlot,
  excludeSessionId?: string,
  excludeSessionGroupId?: string,
): Promise<SlotAvailabilityById> {
  const entries = await Promise.all(
    tutors.map(async (tutor) => {
      const entry = await checkOneTutorSlotAvailability(
        tutor.id,
        slot,
        excludeSessionId,
        excludeSessionGroupId,
      );
      return [tutor.id, entry] as const;
    }),
  );
  return Object.fromEntries(entries);
}

/** Tutors whose latest check returned conflict. */
function getUnavailableTutors(
  tutors: UserWithProfile[],
  availabilityById: SlotAvailabilityById,
): UserWithProfile[] {
  return tutors.filter((t) => availabilityById[t.id]?.status === 'conflict');
}

/** Number of tutors whose latest check returned conflict. */
function countConflicts(availabilityById: SlotAvailabilityById): number {
  return Object.values(availabilityById).filter((e) => e.status === 'conflict').length;
}

/** Sync reasons a tutor cannot be added (slot, cap, duplicate). */
export function getTutorAddBlockReason(params: {
  user: UserWithProfile;
  slot: SessionSlot;
  selectedTutors: UserWithProfile[];
  maxTutors: number | null;
  tutorLimitMessage: (name: string) => string;
}): Extract<AddParticipantOutcome, { ok: false }> | null {
  const { user, slot, selectedTutors, maxTutors, tutorLimitMessage } = params;
  const name = formatTutorDisplayName(user);

  if (!isSessionSlotComplete(slot)) {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage('selectDateSlotBeforeTutor'),
    };
  }
  if (maxTutors !== null && selectedTutors.length >= maxTutors) {
    return { ok: false, kind: 'warning', message: tutorLimitMessage(name) };
  }
  if (selectedTutors.some((t) => t.id === user.id)) {
    return {
      ok: false,
      kind: 'info',
      message: getErrorMessage({ code: 'participantAlreadyNamedTutor', name }),
    };
  }
  return null;
}

/** Async slot check before committing a tutor add. */
export async function ensureTutorAvailableForAdd(params: {
  user: UserWithProfile;
  slot: SessionSlot;
  excludeSessionId?: string;
  excludeSessionGroupId?: string;
  successMessage?: string;
}): Promise<AddParticipantOutcome> {
  const { user, slot, excludeSessionId, excludeSessionGroupId, successMessage } = params;
  const name = formatTutorDisplayName(user);

  const entry = await checkOneTutorSlotAvailability(
    user.id,
    slot,
    excludeSessionId,
    excludeSessionGroupId,
  );

  if (entry.status === 'conflict') {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage({ code: 'alreadyBusyAtSlot', name }),
    };
  }
  if (entry.status === 'unknown') {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage('tutorAvailabilityCheckFailed'),
    };
  }

  if (successMessage) {
    return { ok: true, kind: 'success', message: successMessage };
  }
  return { ok: true };
}

/** Student fallback label when first/last name are missing. */
export function formatStudentDisplayName(user: UserWithProfile): string {
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || getRoleLabel('student');
}

/** Hard-block copy when students have slot conflicts. */
export function buildStudentSubmitBlockedMessage(unavailable: UserWithProfile[]): string {
  if (unavailable.length === 0) return '';
  const names = unavailable.map(formatStudentDisplayName).join(', ');
  if (unavailable.length === 1) {
    return getErrorMessage({ code: 'studentUnavailableAtSlot', names });
  }
  return getErrorMessage({ code: 'studentsUnavailableAtSlot', names });
}

/** Live hint while student availability is checking or conflicting. */
export function buildStudentSoftHintMessage(
  conflictCount: number,
  isChecking: boolean,
): string | null {
  if (isChecking) return getErrorMessage('studentAvailabilityChecking');
  if (conflictCount === 1) return getErrorMessage('studentSlotConflictOne');
  if (conflictCount > 1) {
    return getErrorMessage({ code: 'studentSlotConflictMany', count: conflictCount });
  }
  return null;
}

/** Probes one student slot; unknown on network failure. */
async function checkOneStudentSlotAvailability(
  studentId: string,
  slot: SessionSlot,
  excludeSessionId?: string,
  excludeSessionGroupId?: string,
): Promise<SlotAvailabilityEntry> {
  try {
    const result = await checkStudentAvailability({
      studentId,
      sessionDate: slot.sessionDate,
      startTime: slot.startTime,
      endTime: slot.endTime,
      excludeSessionId,
      excludeSessionGroupId,
    });
    return result.available
      ? { status: 'available' }
      : { status: 'conflict', conflictingSession: result.conflictingSession };
  } catch {
    return { status: 'unknown' };
  }
}

/** Parallel availability map for all selected students. */
async function checkStudentsSlotAvailability(
  students: UserWithProfile[],
  slot: SessionSlot,
  excludeSessionId?: string,
  excludeSessionGroupId?: string,
): Promise<SlotAvailabilityById> {
  const entries = await Promise.all(
    students.map(async (student) => {
      const entry = await checkOneStudentSlotAvailability(
        student.id,
        slot,
        excludeSessionId,
        excludeSessionGroupId,
      );
      return [student.id, entry] as const;
    }),
  );
  return Object.fromEntries(entries);
}

/** Students whose latest check returned conflict. */
function getUnavailableStudents(
  students: UserWithProfile[],
  availabilityById: SlotAvailabilityById,
): UserWithProfile[] {
  return students.filter((s) => availabilityById[s.id]?.status === 'conflict');
}

/** Number of students whose latest check returned conflict. */
function countStudentConflicts(availabilityById: SlotAvailabilityById): number {
  return Object.values(availabilityById).filter((e) => e.status === 'conflict').length;
}

/** Sync reasons a student cannot be added (slot, cap, duplicate). */
export function getStudentAddBlockReason(params: {
  user: UserWithProfile;
  slot: SessionSlot;
  selectedStudents: UserWithProfile[];
  maxStudents: number | null;
}): Extract<AddParticipantOutcome, { ok: false }> | null {
  const { user, slot, selectedStudents, maxStudents } = params;
  const name = formatStudentDisplayName(user);

  if (!isSessionSlotComplete(slot)) {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage('selectDateSlotBeforeStudent'),
    };
  }
  if (maxStudents !== null && selectedStudents.length >= maxStudents) {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage({ code: 'studentCapacityReachedWithMax', maxStudents }),
    };
  }
  if (selectedStudents.some((s) => s.id === user.id)) {
    return {
      ok: false,
      kind: 'info',
      message: getErrorMessage({ code: 'participantAlreadyNamedStudent', name }),
    };
  }
  return null;
}

/** Async slot check before committing a student add. */
export async function ensureStudentAvailableForAdd(params: {
  user: UserWithProfile;
  slot: SessionSlot;
  excludeSessionId?: string;
  excludeSessionGroupId?: string;
  successMessage?: string;
}): Promise<AddParticipantOutcome> {
  const { user, slot, excludeSessionId, excludeSessionGroupId, successMessage } = params;
  const name = formatStudentDisplayName(user);

  const entry = await checkOneStudentSlotAvailability(
    user.id,
    slot,
    excludeSessionId,
    excludeSessionGroupId,
  );

  if (entry.status === 'conflict') {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage({ code: 'alreadyBusyAtSlot', name }),
    };
  }
  if (entry.status === 'unknown') {
    return {
      ok: false,
      kind: 'warning',
      message: getErrorMessage('studentAvailabilityCheckFailed'),
    };
  }

  if (successMessage) {
    return { ok: true, kind: 'success', message: successMessage };
  }
  return { ok: true };
}

/** Wait before hitting the slot-check API while the user is still editing the slot. */
const AVAILABILITY_DEBOUNCE_MS = 400;

/** Debounced tutor conflict map plus submit-time revalidation. */
export function useSessionTutorAvailability({
  tutors,
  slot,
  excludeSessionId,
  excludeSessionGroupId,
  enabled = true,
}: UseSessionTutorAvailabilityOptions) {
  const [availabilityById, setAvailabilityById] = React.useState<SlotAvailabilityById>({});
  const [isChecking, setIsChecking] = React.useState(false);
  /** Ignore a response if a newer check has already started. */
  const checkGenerationRef = React.useRef(0);

  const slotComplete = isSessionSlotComplete(slot);
  /** Stable effect key — tutor identity list, not the array reference. */
  const tutorIdsKey = tutors.map((t) => t.id).join(',');
  const slotKey = `${slot.sessionDate}|${slot.startTime}|${slot.endTime}`;

  React.useEffect(() => {
    if (!enabled || !slotComplete || tutors.length === 0) {
      setAvailabilityById({});
      setIsChecking(false);
      return;
    }

    const generation = ++checkGenerationRef.current;
    setIsChecking(true);

    setAvailabilityById((prev) => {
      const next: SlotAvailabilityById = { ...prev };
      for (const tutor of tutors) {
        next[tutor.id] = { status: 'checking' };
      }
      return next;
    });

    const timer = window.setTimeout(async () => {
      try {
        const result = await checkTutorsSlotAvailability(
          tutors,
          slot,
          excludeSessionId,
          excludeSessionGroupId,
        );
        if (checkGenerationRef.current !== generation) return;
        setAvailabilityById(result);
      } finally {
        if (checkGenerationRef.current === generation) {
          setIsChecking(false);
        }
      }
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    enabled,
    slotComplete,
    tutorIdsKey,
    slotKey,
    excludeSessionId,
    excludeSessionGroupId,
    tutors,
    slot,
  ]);

  const conflictCount = React.useMemo(() => countConflicts(availabilityById), [availabilityById]);

  const hasConflicts = conflictCount > 0;

  /** Banner while checking or when at least one tutor conflicts. */
  const softHint = React.useMemo(() => {
    if (!enabled || !slotComplete || tutors.length === 0) return null;
    return buildSoftHintMessage(conflictCount, isChecking);
  }, [enabled, slotComplete, tutors.length, conflictCount, isChecking]);

  /** Fresh API check at submit — the debounce snapshot may be outdated. */
  const validateForSubmit = React.useCallback(async (): Promise<
    { ok: true } | { ok: false; unavailable: UserWithProfile[] }
  > => {
    if (!slotComplete || tutors.length === 0) {
      return { ok: true };
    }

    const fresh = await checkTutorsSlotAvailability(
      tutors,
      slot,
      excludeSessionId,
      excludeSessionGroupId,
    );
    setAvailabilityById(fresh);

    const unavailable = getUnavailableTutors(tutors, fresh);
    if (unavailable.length > 0) {
      return { ok: false, unavailable };
    }
    return { ok: true };
  }, [slotComplete, tutors, slot, excludeSessionId, excludeSessionGroupId]);

  /** Latest slot-check entry for one tutor (checking / ok / conflict). */
  const getEntry = React.useCallback(
    (tutorId: string): SlotAvailabilityEntry | undefined => availabilityById[tutorId],
    [availabilityById],
  );

  return {
    availabilityById,
    isChecking,
    hasConflicts,
    conflictCount,
    softHint,
    slotComplete,
    validateForSubmit,
    getEntry,
  };
}

/** Debounced student conflict map plus submit-time revalidation. */
export function useSessionStudentAvailability({
  students,
  slot,
  excludeSessionId,
  excludeSessionGroupId,
  enabled = true,
}: UseSessionStudentAvailabilityOptions) {
  const [availabilityById, setAvailabilityById] = React.useState<SlotAvailabilityById>({});
  const [isChecking, setIsChecking] = React.useState(false);
  /** Ignore a response if a newer check has already started. */
  const checkGenerationRef = React.useRef(0);

  const slotComplete = isSessionSlotComplete(slot);
  /** Stable effect key — student identity list, not the array reference. */
  const studentIdsKey = students.map((s) => s.id).join(',');
  const slotKey = `${slot.sessionDate}|${slot.startTime}|${slot.endTime}`;

  React.useEffect(() => {
    if (!enabled || !slotComplete || students.length === 0) {
      setAvailabilityById({});
      setIsChecking(false);
      return;
    }

    const generation = ++checkGenerationRef.current;
    setIsChecking(true);

    setAvailabilityById((prev) => {
      const next: SlotAvailabilityById = { ...prev };
      for (const student of students) {
        next[student.id] = { status: 'checking' };
      }
      return next;
    });

    const timer = window.setTimeout(async () => {
      try {
        const result = await checkStudentsSlotAvailability(
          students,
          slot,
          excludeSessionId,
          excludeSessionGroupId,
        );
        if (checkGenerationRef.current !== generation) return;
        setAvailabilityById(result);
      } finally {
        if (checkGenerationRef.current === generation) {
          setIsChecking(false);
        }
      }
    }, AVAILABILITY_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    enabled,
    slotComplete,
    studentIdsKey,
    slotKey,
    excludeSessionId,
    excludeSessionGroupId,
    students,
    slot,
  ]);

  const conflictCount = React.useMemo(
    () => countStudentConflicts(availabilityById),
    [availabilityById],
  );

  const hasConflicts = conflictCount > 0;

  /** Banner while checking or when at least one student conflicts. */
  const softHint = React.useMemo(() => {
    if (!enabled || !slotComplete || students.length === 0) return null;
    return buildStudentSoftHintMessage(conflictCount, isChecking);
  }, [enabled, slotComplete, students.length, conflictCount, isChecking]);

  /** Fresh API check at submit — the debounce snapshot may be outdated. */
  const validateForSubmit = React.useCallback(async (): Promise<
    { ok: true } | { ok: false; unavailable: UserWithProfile[] }
  > => {
    if (!slotComplete || students.length === 0) {
      return { ok: true };
    }

    const fresh = await checkStudentsSlotAvailability(
      students,
      slot,
      excludeSessionId,
      excludeSessionGroupId,
    );
    setAvailabilityById(fresh);

    const unavailable = getUnavailableStudents(students, fresh);
    if (unavailable.length > 0) {
      return { ok: false, unavailable };
    }
    return { ok: true };
  }, [slotComplete, students, slot, excludeSessionId, excludeSessionGroupId]);

  /** Latest slot-check entry for one student (checking / ok / conflict). */
  const getEntry = React.useCallback(
    (studentId: string): SlotAvailabilityEntry | undefined => availabilityById[studentId],
    [availabilityById],
  );

  return {
    availabilityById,
    isChecking,
    hasConflicts,
    conflictCount,
    softHint,
    slotComplete,
    validateForSubmit,
    getEntry,
  };
}

/** Prefers pointer hits on tutor chips over closest-center. */
export const sessionFormCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    const tutorChipCollision = pointerCollisions.find(
      (c) => c.data?.droppableContainer?.data?.current?.type === 'tutor-chip',
    );
    if (tutorChipCollision) return [tutorChipCollision];
    return pointerCollisions;
  }
  return closestCenter(args);
};

/** Today-only: start cannot be earlier than now. */
export function getMinStartTimeForSessionDate(sessionDate: string): string | undefined {
  if (!sessionDate) return undefined;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (sessionDate !== today) return undefined;
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** End time picker floor — same as start when set. */
export function getMinEndTime(startTime: string): string | undefined {
  return startTime || undefined;
}

/** Prepends a stub option so a missing establishment stays selectable. */
export function appendFallbackEstablishmentOption(
  options: FormSelectOption[],
  establishmentId: string,
  fallbackEstablishment?: Establishment | null,
  fallbackEstablishments?: Establishment[],
): FormSelectOption[] {
  if (!establishmentId || options.some((opt) => opt.value === establishmentId)) {
    return options;
  }
  const fromList = fallbackEstablishments?.find((e) => e.id === establishmentId);
  const resolved =
    fromList ?? (fallbackEstablishment?.id === establishmentId ? fallbackEstablishment : undefined);
  if (!resolved) return options;
  return [{ value: resolved.id, label: formatEstablishmentSelectLabel(resolved) }, ...options];
}

/** Classes whose level is in the selected set; empty levels → none. */
export function filterSchoolClassesByLevels<T extends { levelId?: string | null }>(
  classes: T[],
  levelIds: string[],
): T[] {
  if (levelIds.length === 0) return [];
  const levelIdSet = new Set(levelIds);
  return classes.filter((sc) => sc.levelId && levelIdSet.has(sc.levelId));
}

/** Specialties linked to at least one selected level. */
export function buildSpecialtyOptionsForLevels(
  specialties: Array<{
    id: string;
    label: string;
    levelLinks?: Array<{ level: { id: string } }>;
  }>,
  levelIds: string[],
): FormSelectOption[] {
  if (levelIds.length === 0) return [];
  const selectedLevelIdSet = new Set(levelIds);
  return specialties
    .filter((s) => s.levelLinks?.some((ll) => selectedLevelIdSet.has(ll.level.id)))
    .map((s) => ({ value: s.id, label: s.label }));
}

/** Map catalog items to `{ value, label }` select options. */
export function toIdLabelOptions<T extends { id: string }>(
  items: T[],
  getLabel: (item: T) => string,
): FormSelectOption[] {
  return items.map((item) => ({ value: item.id, label: getLabel(item) }));
}

/** Prepend an empty placeholder option to a select list. */
export function withEmptySelectOption(
  placeholder: string,
  options: FormSelectOption[],
): FormSelectOption[] {
  return [{ value: '', label: placeholder }, ...options];
}

/** Classrooms for the chosen establishment, plus empty placeholder. */
export function buildClassroomSelectOptions(
  classrooms: Array<{ id: string; establishmentId?: string; label?: string | null; name?: string }>,
  establishmentId: string,
): FormSelectOption[] {
  return withEmptySelectOption(
    'Sélectionner une salle',
    classrooms
      .filter((r) => !establishmentId || r.establishmentId === establishmentId)
      .map((r) => ({ value: r.id, label: r.label || r.name || r.id })),
  );
}

/** Clears class and specialties when establishment or levels change. */
export function applySessionFieldSideEffects<
  T extends { classId: string; specialtyIds: string[]; recurrenceRule?: unknown },
>(data: T, field: string): T {
  if (field === 'establishmentId' || field === 'levelIds') {
    return { ...data, classId: '', specialtyIds: [] };
  }
  if (field === 'recurrenceType') {
    return { ...data, recurrenceRule: null };
  }
  return data;
}

/** Effective tutor cap for create vs edit / group / individual. */
export function resolveMaxTutors(params: {
  mode: 'create' | 'edit';
  planificationType?: PlanificationType;
  isEditGroup: boolean;
  sessionType: SessionType | '';
  activityMaxTutors?: number | null;
}): number | null {
  if (params.mode === 'edit') {
    return getMaxTutorsForSessionType(params.sessionType, params.activityMaxTutors);
  }
  if (params.planificationType === 'individual') return 1;
  if (params.isEditGroup) return null;
  return params.activityMaxTutors ?? null;
}

/** Student cap; unlimited while editing a group. */
export function resolveMaxStudents(params: {
  isEditGroup: boolean;
  activityMaxStudents?: number | null;
}): number | null {
  if (params.isEditGroup) return null;
  return params.activityMaxStudents ?? null;
}

/** Cap message for create-mode planification (individual vs activity max). */
export function tutorLimitMessageForCreate(
  planificationType: PlanificationType | undefined,
  maxTutors: number | null,
  _incomingName?: string,
): string {
  if (planificationType === 'individual') {
    return getErrorMessage('individualPlanificationSingleTutor');
  }
  return getErrorMessage({ code: 'maxTutorsCapacityReached', max: maxTutors ?? 0 });
}

/** Create button label for queued / pending / idle. */
export function getCreateSessionButtonLabel(queued: boolean, pending: boolean): string {
  if (queued) return 'Création en cours…';
  if (pending) return 'Création…';
  return 'Créer';
}

/** Seeds every participant as present (create-mode default). */
export function buildPresentAttendance(
  tutors: UserWithProfile[],
  students: UserWithProfile[],
): ParticipantAttendanceById {
  const map: ParticipantAttendanceById = {};
  for (const t of tutors) map[t.id] = { status: 'present', justification: null };
  for (const s of students) map[s.id] = { status: 'present', justification: null };
  return map;
}

/** Present map on create; empty on edit (server already has attendance). */
export function createModeAttendance(
  mode: 'create' | 'edit',
  tutors: UserWithProfile[],
  students: UserWithProfile[],
): ParticipantAttendanceById {
  if (mode !== 'create') return {};
  return buildPresentAttendance(tutors, students);
}

/** Sum of tutor + student + volunteer ids across DTOs (heavy-create sizing). */
export function countDtoParticipants(
  dtos: Array<{ tutorIds?: string[]; studentIds?: string[]; volunteerIds?: string[] }>,
): number {
  return dtos.reduce(
    (sum, d) =>
      sum + (d.tutorIds?.length ?? 0) + (d.studentIds?.length ?? 0) + (d.volunteerIds?.length ?? 0),
    0,
  );
}

/** Required code/label (and type) when inline activity create is open. */
export function validateInlineActivityForms(params: {
  showActivityForm: boolean;
  showActivityTypeForm: boolean;
  newActivityData: Partial<CreateActivityDto>;
  newActivityTypeData: Partial<CreateActivityTypeDto>;
}): { valid: boolean; message?: string } {
  if (!params.showActivityForm) return { valid: true };
  if (!params.newActivityData.code || !params.newActivityData.label) {
    return { valid: false, message: getErrorMessage('requiredActivityFields') };
  }
  if (params.showActivityTypeForm) {
    if (!params.newActivityTypeData.code || !params.newActivityTypeData.label) {
      return {
        valid: false,
        message: getErrorMessage('requiredActivityType'),
      };
    }
  } else if (!params.newActivityData.activityTypeId) {
    return {
      valid: false,
      message: getErrorMessage('selectActivityType'),
    };
  }
  return { valid: true };
}

/** Existing id vs nested new activity / type for the create DTO. */
export function buildInlineActivityPayload(params: {
  showActivityForm: boolean;
  showActivityTypeForm: boolean;
  activityId: string;
  newActivityData: Partial<CreateActivityDto>;
  newActivityTypeData: Partial<CreateActivityTypeDto>;
}): {
  activityId: string | null;
  newActivity: CreateActivityDto | undefined;
  newActivityType: CreateActivityTypeDto | undefined;
} {
  if (!params.showActivityForm) {
    return {
      activityId: params.activityId || null,
      newActivity: undefined,
      newActivityType: undefined,
    };
  }
  if (params.showActivityTypeForm) {
    return {
      activityId: null,
      newActivity: { ...params.newActivityData, activityTypeId: undefined } as CreateActivityDto,
      newActivityType: params.newActivityTypeData as CreateActivityTypeDto,
    };
  }
  return {
    activityId: null,
    newActivity: {
      ...params.newActivityData,
      activityTypeId: params.newActivityData.activityTypeId,
    } as CreateActivityDto,
    newActivityType: undefined,
  };
}

/** Looks up establishment, program, activity, and classroom by current ids. */
export function getSelectedSessionEntities(params: {
  establishmentId: string;
  programId: string;
  activityId: string;
  roomId: string;
  establishments: Array<{ id: string; name?: string }>;
  programs: Array<{ id: string; name?: string }>;
  activities: Array<{ id: string; label?: string }>;
  classrooms: Array<{ id: string; name?: string; label?: string }>;
}) {
  return {
    establishment: params.establishments.find((e) => e.id === params.establishmentId),
    program: params.programs.find((p) => p.id === params.programId),
    activity: params.activities.find((a) => a.id === params.activityId),
    classroom: params.classrooms.find((r) => r.id === params.roomId),
  };
}

/** studentToTutor map as {studentId, tutorId} rows for the matcher UI. */
export function toSpecificMatches(
  studentToTutor: Record<string, string>,
): Array<{ studentId: string; tutorId: string }> {
  return Object.entries(studentToTutor).map(([studentId, tutorId]) => ({ studentId, tutorId }));
}

/** Students currently assigned to one facilitator. */
export function getStudentsMatchedToTutor(
  students: UserWithProfile[],
  studentToTutor: Record<string, string>,
  tutorId: string,
): UserWithProfile[] {
  return students.filter((s) => studentToTutor[s.id] === tutorId);
}

/** Students with no entry in the matching map. */
export function getOrphanStudents(
  students: UserWithProfile[],
  studentToTutor: Record<string, string>,
): UserWithProfile[] {
  return students.filter((s) => !studentToTutor[s.id]);
}

/** VSC tutor profiles not owning a pair. */
export function getFloatingVscTutors(
  tutors: UserWithProfile[],
  studentToTutor: Record<string, string>,
): UserWithProfile[] {
  const ids = new Set(getFloatingVscTutorIds(tutors, studentToTutor));
  return tutors.filter((t) => ids.has(t.id));
}

/** Groups auto-assign proposals under each selected tutor. */
export function groupProposalPairsByTutor(
  tutors: UserWithProfile[],
  proposedPairs?: Array<{
    tutorUserId: string;
    studentUserId: string;
    source: ProposedPairSource;
  }>,
): Array<{
  tutor: UserWithProfile;
  pairs: Array<{ studentId: string; source: ProposedPairSource }>;
}> {
  const map = new Map<string, Array<{ studentId: string; source: ProposedPairSource }>>();
  proposedPairs?.forEach((pair) => {
    const list = map.get(pair.tutorUserId) ?? [];
    list.push({ studentId: pair.studentUserId, source: pair.source });
    map.set(pair.tutorUserId, list);
  });
  return tutors.map((tutor) => ({
    tutor,
    pairs: map.get(tutor.id) ?? [],
  }));
}

/** Proposal rows → studentId → tutorId map. */
export function proposalPairsToMapping(
  pairs: Array<{ studentUserId: string; tutorUserId: string }>,
): Record<string, string> {
  const mapping: Record<string, string> = {};
  pairs.forEach((p) => {
    mapping[p.studentUserId] = p.tutorUserId;
  });
  return mapping;
}

/** Proposal-card name with a generic fallback. */
export function formatProposalUserName(user?: UserWithProfile | null): string {
  return user ? formatSelectedUserLabel(user) : 'Utilisateur';
}

/** Seeds facilitator role from the participant row, then applies volunteer/tutor mapping. */
function facilitatorFromParticipant(p: { role: string; user: UserWithProfile }): UserWithProfile {
  const seeded = {
    ...p.user,
    currentRole: (p.role === 'volunteer' ? 'volunteer' : 'tutor') as 'tutor' | 'volunteer',
  };
  return { ...seeded, currentRole: facilitatorRoleForSession(seeded) };
}

/** Hydrates the form from an existing session (local date, in_person default). */
export function sessionToFormData(session: {
  establishmentId?: string | null;
  establishment?: { id: string } | null;
  schoolYearId?: string | null;
  roomId?: string | null;
  programId?: string | null;
  levelIds?: string[] | null;
  subjectIds?: string[] | null;
  classId?: string | null;
  specialtyIds?: string[] | null;
  activityId?: string | null;
  sessionDate: Date | string;
  startTime: string;
  endTime: string;
  type: SessionType;
  sessionFormat?: SessionFormat | null;
  mode: SessionMode;
  recurrenceType: RecurrenceType;
  recurrenceRule?: Record<string, unknown> | null;
  topic?: string | null;
  joinLink?: string | null;
  planDuringHolidays?: boolean;
  internalNotes?: string | null;
}): SessionFormData {
  return {
    establishmentId: session.establishmentId ?? session.establishment?.id ?? '',
    schoolYearId: session.schoolYearId || '',
    roomId: session.roomId || '',
    programId: session.programId || '',
    levelIds: session.levelIds ?? [],
    subjectIds: session.subjectIds ?? [],
    classId: session.classId || '',
    specialtyIds: session.specialtyIds ?? [],
    activityId: session.activityId || '',
    sessionDate: toLocalDateString(new Date(session.sessionDate)),
    startTime: session.startTime,
    endTime: session.endTime,
    type: session.type,
    sessionFormat: session.sessionFormat ?? ('in_person' as SessionFormat),
    mode: session.mode,
    recurrenceType: session.recurrenceType,
    recurrenceRule: session.recurrenceRule ?? null,
    topic: session.topic || '',
    joinLink: session.joinLink || '',
    planDuringHolidays: session.planDuringHolidays ?? true,
    internalNotes: session.internalNotes || '',
  };
}

/** Splits participants into facilitators, students, and attendance map. */
export function extractSessionParticipants(
  participants: Array<{
    userId: string;
    role: string;
    attendanceStatus?: string | null;
    absenceJustification?: string | null;
    user: UserWithProfile;
  }>,
): {
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  attendance: ParticipantAttendanceById;
} {
  const tutors = participants
    .filter((p) => p.role === 'tutor' || p.role === 'volunteer')
    .map(facilitatorFromParticipant);
  const students = participants
    .filter((p) => p.role === 'student')
    .map((p) => ({ ...p.user, currentRole: 'student' as const }));
  const attendance: ParticipantAttendanceById = {};
  for (const p of participants) {
    if (!p.userId) continue;
    attendance[p.userId] = {
      status: normalizeAttendanceStatus(p.attendanceStatus),
      justification: p.absenceJustification ?? null,
    };
  }
  return { tutors, students, attendance };
}

/** Active (non-cancelled) children, or all children if every one is cancelled. */
function editGroupActiveChildren(editGroup: SessionGroup): SessionGroupChild[] {
  const activeChildren = editGroup.sessions.filter((c) => c.status !== 'CANCELLED');
  if (activeChildren.length > 0) return activeChildren;
  return editGroup.sessions;
}

/** Form fields from a group plus the first child as the time/classroom fallback. */
function editGroupToFormData(
  editGroup: SessionGroup,
  ref: SessionGroupChild | undefined,
): SessionFormData {
  return {
    establishmentId: editGroup.establishmentId ?? '',
    schoolYearId: editGroup.schoolYearId ?? ref?.schoolYearId ?? '',
    roomId: ref?.roomId ?? '',
    programId: editGroup.programId ?? ref?.programId ?? '',
    levelIds: editGroup.levelIds ?? ref?.levelIds ?? [],
    subjectIds: editGroup.subjectIds ?? ref?.subjectIds ?? [],
    classId: ref?.classId ?? '',
    specialtyIds: ref?.specialtyIds ?? [],
    activityId: editGroup.activityId ?? ref?.activityId ?? '',
    sessionDate: editGroup.sessionDate ? editGroup.sessionDate.slice(0, 10) : '',
    startTime: (editGroup.startTime ?? '').slice(0, 5),
    endTime: (editGroup.endTime ?? '').slice(0, 5),
    type: editGroup.type as SessionType | '',
    sessionFormat: 'in_person' as SessionFormat,
    mode: (ref?.mode ?? 'single') as SessionMode | '',
    recurrenceType: (ref?.recurrenceType ?? 'none') as RecurrenceType,
    recurrenceRule: (ref?.recurrenceRule ?? null) as Record<string, unknown> | null,
    topic: ref?.topic ?? '',
    joinLink: ref?.joinLink ?? '',
    planDuringHolidays: ref?.planDuringHolidays ?? true,
    internalNotes: ref?.internalNotes ?? '',
  };
}

/** Collect unique tutor/volunteer facilitators from a child session. */
function collectChildFacilitators(
  child: SessionGroupChild,
  tutorMap: Map<string, UserWithProfile>,
): void {
  for (const p of child.participants ?? []) {
    if (p.role !== 'tutor' && p.role !== 'volunteer') continue;
    if (tutorMap.has(p.userId)) continue;
    tutorMap.set(p.userId, facilitatorFromParticipant(p));
  }
}

/** Collect unique students from a child session. */
function collectChildStudents(
  child: SessionGroupChild,
  studentMap: Map<string, UserWithProfile>,
): void {
  for (const p of child.participants ?? []) {
    if (p.role !== 'student') continue;
    if (studentMap.has(p.userId)) continue;
    studentMap.set(p.userId, { ...p.user, currentRole: 'student' as const });
  }
}

/** Record student→tutor pairs and tutor→child ownership. */
function collectChildPairs(
  child: SessionGroupChild,
  studentToTutor: Record<string, string>,
  tutorToChildId: Record<string, string>,
): void {
  for (const pair of child.studentTutorPairs ?? []) {
    studentToTutor[pair.studentUserId] = pair.tutorUserId;
    tutorToChildId[pair.tutorUserId] = child.id;
  }
}

/** Assign the child's first facilitator as owner when not already mapped. */
function assignOwnerChildId(
  child: SessionGroupChild,
  tutorToChildId: Record<string, string>,
): void {
  const ownerP = (child.participants ?? []).find(
    (p) => p.role === 'tutor' || p.role === 'volunteer',
  );
  if (!ownerP || tutorToChildId[ownerP.userId]) return;
  tutorToChildId[ownerP.userId] = child.id;
}

/** Union tutors, students, pairs, and ownership across group children. */
function collectEditGroupComposition(children: SessionGroupChild[]): {
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  studentToTutor: Record<string, string>;
  tutorToChildId: Record<string, string>;
  soleChildId: string | null;
} {
  const tutorMap = new Map<string, UserWithProfile>();
  const studentMap = new Map<string, UserWithProfile>();
  const tutorToChildId: Record<string, string> = {};
  const studentToTutor: Record<string, string> = {};

  for (const child of children) {
    collectChildFacilitators(child, tutorMap);
    collectChildStudents(child, studentMap);
    collectChildPairs(child, studentToTutor, tutorToChildId);
    assignOwnerChildId(child, tutorToChildId);
  }

  return {
    tutors: Array.from(tutorMap.values()),
    students: Array.from(studentMap.values()),
    studentToTutor,
    tutorToChildId,
    soleChildId: children[0]?.id ?? null,
  };
}

/** Form + composition from a session group (skips cancelled children). */
export function editGroupToPrefill(editGroup: SessionGroup): {
  formData: SessionFormData;
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  studentToTutor: Record<string, string>;
  tutorToChildId: Record<string, string>;
  soleChildId: string | null;
} {
  const children = editGroupActiveChildren(editGroup);
  return {
    formData: editGroupToFormData(editGroup, children[0]),
    ...collectEditGroupComposition(children),
  };
}

/** Stub establishment so the select keeps the current id visible. */
export function resolveSelectedEstablishmentForForm(params: {
  mode: 'create' | 'edit';
  selectedEstablishmentId: string;
  selectedEstablishmentDetail?: Establishment | null;
  createFallbackName?: string;
  sessionEstablishment?: Pick<Establishment, 'id' | 'name'> | null;
  sessionEstablishmentName?: string;
}): Establishment | undefined {
  if (params.mode === 'create') {
    return stubEstablishmentForSelect(
      params.selectedEstablishmentId,
      params.selectedEstablishmentDetail,
      params.createFallbackName,
    ) as Establishment | undefined;
  }
  return stubEstablishmentForSelect(
    params.selectedEstablishmentId,
    params.selectedEstablishmentDetail ?? params.sessionEstablishment,
    params.sessionEstablishmentName,
  ) as Establishment | undefined;
}

/** True when the drag payload is a tutor or volunteer. */
export function isTutorDragSource(roleGroup?: string, activeType?: string): boolean {
  return (
    roleGroup === 'tutors' ||
    roleGroup === 'volunteers' ||
    activeType === 'tutor' ||
    activeType === 'volunteer'
  );
}

/** True when the drag payload is a student. */
export function isStudentDragSource(roleGroup?: string, activeType?: string): boolean {
  return roleGroup === 'students' || activeType === 'student';
}

/** Adds immediately, then reverts if the async availability check fails. */
export async function addParticipantIfAvailable(
  ensure: (onSyncChecksPassed: () => void, successMessage?: string) => Promise<boolean>,
  onAdded: () => void,
  onRevert: () => void,
  successMessage: string,
): Promise<void> {
  let added = false;
  const ok = await ensure(() => {
    added = true;
    onAdded();
  }, successMessage);
  if (!ok && added) onRevert();
}

/** Toast after a successful tutor/student add. */
export function addedParticipantMessage(user: UserWithProfile, role: 'tutor' | 'student'): string {
  const roleLabel = role === 'tutor' ? 'tuteur' : 'élève';
  return `${user.firstName} ${user.lastName} ajouté(e) comme ${roleLabel}`;
}

/** Suffix showing created / remaining sessions in a queued job. */
export function formatQueuedCreateProgressLabel(created: number, total: number): string {
  if (created > 0) {
    const totalPart = total > 0 ? ` / ${total}` : '';
    return ` — ${created}${totalPart} séance(s) créée(s)`;
  }
  if (total > 1) return ` — ${total} séances à créer`;
  return '…';
}

/** Footer: proposed pairs and leftover unassigned students. */
export function formatAutoAssignmentFooterLabel(
  isLoading: boolean,
  totalAssigned: number,
  unassignedCount: number,
): string {
  if (isLoading) return 'Calcul en cours…';
  if (unassignedCount > 0) {
    return `${totalAssigned} binôme(s) proposé(s) · ${unassignedCount} élève(s) sans tuteur`;
  }
  return `${totalAssigned} binôme(s) proposé(s)`;
}

/** True when mode is recurring and recurrenceType is a real series. */
export function isRecurringSessionSelection(data: {
  mode: SessionMode | '';
  recurrenceType: RecurrenceType;
}): boolean {
  return (
    data.mode === 'recurring' && data.recurrenceType !== 'none' && data.recurrenceType !== 'once'
  );
}

/** Predicted session count for the heavy-create warning. */
export function estimateCreateSessionCount(
  isRecurringSelected: boolean,
  recurrenceCount: number | undefined,
  plannedItemCount: number,
): number {
  if (isRecurringSelected) return (recurrenceCount ?? 0) * plannedItemCount;
  return plannedItemCount;
}

/** Whether to show this-occurrence vs whole-series scope. */
export function shouldShowRecurrenceScope(params: {
  mode: 'create' | 'edit';
  isEditGroup: boolean;
  isRecurringSeries: boolean;
  recurrenceGroupId?: string | null;
  sessionMode?: string;
}): boolean {
  if (params.mode === 'create') return params.isEditGroup && params.isRecurringSeries;
  return !!(params.recurrenceGroupId && params.sessionMode === 'recurring');
}

/** DnD context is needed on edit, or on create once participants show. */
export function shouldWrapTutoringDnd(mode: 'create' | 'edit', showParticipants: boolean): boolean {
  return mode === 'edit' || showParticipants;
}

/** Group/create uses chip-aware detection; classic edit uses closest-center. */
export function resolveTutoringCollisionDetection<T>(
  mode: 'create' | 'edit',
  isGroupMode: boolean,
  groupDetection: T,
  classicDetection: T,
): T {
  if (mode === 'create' || isGroupMode) return groupDetection;
  return classicDetection;
}

/** Picks create vs edit attendance source. */
export function attendanceForFormMode<T>(
  mode: 'create' | 'edit',
  createAttendance: T,
  editAttendance: T,
): T {
  if (mode === 'create') return createAttendance;
  return editAttendance;
}

/** Submit lock: invalid form, pending mutation, queued job, or missing subjects. */
export function isTutoringSubmitDisabled(params: {
  mode: 'create' | 'edit';
  isEditGroup: boolean;
  isFormValid: boolean;
  createPending: boolean;
  hasQueuedJob: boolean;
  hasSubjects: boolean;
  updateGroupPending: boolean;
  updatePending: boolean;
}): boolean {
  if (!params.isFormValid) return true;
  if (params.mode === 'edit') return params.updatePending;
  if (params.isEditGroup) return params.updateGroupPending;
  return params.createPending || params.hasQueuedJob || !params.hasSubjects;
}

/** Shared create fields (no participants) plus denormalized names. */
export function buildCreateSessionBaseDto(params: {
  sessionData: SessionFormData;
  activityId?: string;
  newActivity?: CreateActivityDto;
  newActivityType?: CreateActivityTypeDto;
  establishmentName: string | null;
  programName: string | null;
  activityName: string | null;
  roomName: string | null;
}): CreateSessionBaseDto {
  const { sessionData } = params;
  return {
    establishmentId: sessionData.establishmentId,
    schoolYearId: sessionData.schoolYearId || null,
    roomId: sessionData.roomId || null,
    programId: sessionData.programId || null,
    levelIds: sessionData.levelIds,
    subjectIds: sessionData.subjectIds,
    classId: sessionData.classId || null,
    specialtyIds: sessionData.specialtyIds.length > 0 ? sessionData.specialtyIds : null,
    sessionDate: sessionData.sessionDate,
    startTime: sessionData.startTime,
    endTime: sessionData.endTime,
    type: sessionData.type as SessionType,
    sessionFormat: 'in_person' as SessionFormat,
    mode: sessionData.mode || undefined,
    recurrenceType: sessionData.recurrenceType,
    recurrenceRule: sessionData.recurrenceRule,
    activityId: params.activityId,
    establishmentName: params.establishmentName,
    programName: params.programName,
    activityName: params.activityName,
    roomName: params.roomName,
    topic: sessionData.topic || null,
    joinLink: sessionData.joinLink || null,
    planDuringHolidays: sessionData.planDuringHolidays,
    internalNotes: sessionData.internalNotes || null,
    newActivity: params.newActivity,
    newActivityType: params.newActivityType,
  };
}

/** Group: one DTO per owner; classic: one DTO with cartesian pairs. */
export function buildCreateSessionDtos(params: {
  isGroupMode: boolean;
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  studentToTutor: Record<string, string>;
  baseDto: CreateSessionBaseDto;
}): CreateSessionDto[] {
  if (params.isGroupMode) {
    return buildGroupModeSessionDtos(params.tutors, params.studentToTutor, params.baseDto);
  }
  return [
    {
      ...params.baseDto,
      ...buildClassicModeParticipantPayload(params.tutors, params.students),
    },
  ];
}

/** Maps create DTOs onto existing child session ids for group update. */
export function mapCreateDtosToGroupChildren(
  dtos: CreateSessionDto[],
  tutorToChildId: Record<string, string>,
  isGroupMode: boolean,
  soleChildId: string | null,
): UpdateSessionGroupChildDto[] {
  return dtos.map((d) => {
    const ownerId = d.pairs?.[0]?.tutorUserId ?? d.tutorIds?.[0] ?? d.volunteerIds?.[0];
    const mappedId = ownerId ? tutorToChildId[ownerId] : undefined;
    const childSessionId = mappedId ?? (isGroupMode ? undefined : (soleChildId ?? undefined));
    return {
      sessionId: childSessionId,
      tutorIds: d.tutorIds,
      studentIds: d.studentIds,
      volunteerIds: d.volunteerIds,
      pairs: d.pairs,
    };
  });
}

/** Group update body; recurrence scope only on series. */
export function buildUpdateSessionGroupPayload(params: {
  baseDto: CreateSessionBaseDto;
  sessions: UpdateSessionGroupChildDto[];
  isRecurringSeries: boolean;
  recurrenceUpdateScope: RecurrenceUpdateScope;
}): UpdateSessionGroupDto {
  const { baseDto } = params;
  return {
    establishmentId: baseDto.establishmentId,
    schoolYearId: baseDto.schoolYearId || undefined,
    roomId: baseDto.roomId,
    programId: baseDto.programId,
    levelIds: baseDto.levelIds,
    subjectIds: baseDto.subjectIds,
    classId: baseDto.classId,
    specialtyIds: baseDto.specialtyIds,
    sessionDate: baseDto.sessionDate,
    startTime: baseDto.startTime,
    endTime: baseDto.endTime,
    type: baseDto.type,
    sessionFormat: baseDto.sessionFormat,
    activityId: baseDto.activityId,
    joinLink: baseDto.joinLink,
    topic: baseDto.topic,
    planDuringHolidays: baseDto.planDuringHolidays,
    internalNotes: baseDto.internalNotes,
    establishmentName: baseDto.establishmentName,
    programName: baseDto.programName,
    activityName: baseDto.activityName,
    roomName: baseDto.roomName,
    sessions: params.sessions,
    ...(params.isRecurringSeries ? { recurrenceUpdateScope: params.recurrenceUpdateScope } : {}),
  };
}

/** Denormalized names for update DTOs from current selects. */
export function resolveEditEntityNames(params: {
  establishmentDetailName?: string | null;
  sessionData: SessionFormData;
  establishments: Array<{ id: string; name?: string }>;
  programs: Array<{ id: string; name?: string }>;
  activities: Array<{ id: string; label?: string }>;
  classrooms: Array<{ id: string; name?: string; label?: string }>;
}): {
  establishmentName: string | null;
  programName: string | null;
  activityName: string | null;
  roomName: string | null;
} {
  const classroom = params.classrooms.find((r) => r.id === params.sessionData.roomId);
  return {
    establishmentName:
      params.establishmentDetailName ??
      params.establishments.find((e) => e.id === params.sessionData.establishmentId)?.name ??
      null,
    programName: params.programs.find((p) => p.id === params.sessionData.programId)?.name ?? null,
    activityName:
      params.activities.find((a) => a.id === params.sessionData.activityId)?.label ?? null,
    roomName: classroom?.name || classroom?.label || null,
  };
}

/** Single-session update body, optionally with recurrenceUpdateScope. */
export function buildUpdateSessionDto(params: {
  sessionData: SessionFormData;
  activityId?: string;
  newActivity?: CreateActivityDto;
  newActivityType?: CreateActivityTypeDto;
  participantPayload: {
    tutorIds: string[];
    studentIds: string[];
    volunteerIds?: string[];
    pairs?: Array<{ studentUserId: string; tutorUserId: string }>;
  };
  names: {
    establishmentName: string | null;
    programName: string | null;
    activityName: string | null;
    roomName: string | null;
  };
  includeRecurrenceScope: boolean;
  recurrenceUpdateScope: RecurrenceUpdateScope;
}): UpdateSessionDto {
  const { sessionData, participantPayload, names } = params;
  return {
    establishmentId: sessionData.establishmentId,
    roomId: sessionData.roomId || null,
    programId: sessionData.programId || null,
    levelIds: sessionData.levelIds,
    subjectIds: sessionData.subjectIds,
    classId: sessionData.classId || null,
    specialtyIds: sessionData.specialtyIds.length > 0 ? sessionData.specialtyIds : null,
    sessionDate: sessionData.sessionDate,
    startTime: sessionData.startTime,
    endTime: sessionData.endTime,
    type: sessionData.type as SessionType,
    sessionFormat: 'in_person',
    mode: sessionData.mode as SessionMode,
    recurrenceType: sessionData.recurrenceType,
    recurrenceRule: sessionData.recurrenceRule,
    activityId: params.activityId,
    topic: sessionData.topic || null,
    joinLink: sessionData.joinLink || null,
    planDuringHolidays: sessionData.planDuringHolidays,
    internalNotes: sessionData.internalNotes || null,
    tutorIds: participantPayload.tutorIds,
    studentIds: participantPayload.studentIds,
    volunteerIds: participantPayload.volunteerIds,
    pairs: participantPayload.pairs,
    newActivity: params.newActivity,
    newActivityType: params.newActivityType,
    establishmentName: names.establishmentName,
    programName: names.programName,
    activityName: names.activityName,
    roomName: names.roomName,
    ...(params.includeRecurrenceScope
      ? { recurrenceUpdateScope: params.recurrenceUpdateScope }
      : {}),
  };
}

/** Pair an existing student, or add-then-pair after an availability check. */
async function dropStudentOnTutorChip(
  userData: UserWithProfile,
  tutorId: string,
  deps: TutoringDragEndDeps,
): Promise<void> {
  if (deps.selectedStudents.some((s) => s.id === userData.id)) {
    deps.matchStudentToTutor(userData, tutorId);
    return;
  }
  await addParticipantIfAvailable(
    (onSync, message) => deps.ensureStudentAvailable(userData, onSync, message),
    () => deps.matchStudentToTutor(userData, tutorId),
    () => deps.handleRemoveStudent(userData.id),
    addedParticipantMessage(userData, 'student'),
  );
}

/** Add a tutor to the unified drop zone after an availability check. */
async function dropTutorOnUnified(
  userData: UserWithProfile,
  deps: TutoringDragEndDeps,
): Promise<void> {
  await addParticipantIfAvailable(
    (onSync, message) => deps.ensureTutorAvailable(userData, onSync, message),
    () => deps.addTutorToSession(userData),
    () => deps.handleRemoveTutor(userData.id),
    addedParticipantMessage(userData, 'tutor'),
  );
}

/** Add a student to the unified zone; optionally hint if tutors exist. */
async function dropStudentOnUnified(
  userData: UserWithProfile,
  deps: TutoringDragEndDeps,
  warnIfTutors: boolean,
): Promise<boolean> {
  let showAssignHint = false;
  await addParticipantIfAvailable(
    (onSync, message) => deps.ensureStudentAvailable(userData, onSync, message),
    () => {
      deps.addStudentToSession(userData);
      if (warnIfTutors && deps.selectedTutors.length > 0) {
        showAssignHint = true;
      }
    },
    () => deps.handleRemoveStudent(userData.id),
    addedParticipantMessage(userData, 'student'),
  );
  return showAssignHint;
}

/** Route a group-mode drop onto a tutor chip or the unified zone. */
async function runGroupModeDragEnd(
  userData: UserWithProfile,
  isTutorDrag: boolean,
  isStudentDrag: boolean,
  overType: string | undefined,
  tutorId: string | undefined,
  deps: TutoringDragEndDeps,
): Promise<boolean> {
  if (isStudentDrag && overType === 'tutor-chip') {
    if (!tutorId) return false;
    await dropStudentOnTutorChip(userData, tutorId, deps);
    return false;
  }
  if (isTutorDrag && overType === 'matching-dropzone-unified') {
    await dropTutorOnUnified(userData, deps);
    return false;
  }
  if (isStudentDrag && overType === 'matching-dropzone-unified') {
    return dropStudentOnUnified(userData, deps, true);
  }
  return false;
}

/** Route a classic-mode drop onto the unified participants zone. */
async function runClassicModeDragEnd(
  userData: UserWithProfile,
  isTutorDrag: boolean,
  isStudentDrag: boolean,
  dropZoneId: string,
  deps: TutoringDragEndDeps,
): Promise<boolean> {
  if (dropZoneId !== 'unified-participants-drop') return false;
  if (isTutorDrag) {
    await dropTutorOnUnified(userData, deps);
    return false;
  }
  if (isStudentDrag) {
    return dropStudentOnUnified(userData, deps, false);
  }
  return false;
}

/** Routes a drop onto tutor chips vs unified zone (group or classic). */
export async function runTutoringDragEnd(
  event: DragEndEvent,
  deps: TutoringDragEndDeps,
): Promise<TutoringDragEndOutcome> {
  const { active, over } = event;
  if (!over) return {};
  const userData = active.data.current?.user as UserWithProfile;
  const roleGroup = active.data.current?.roleGroup as string | undefined;
  const activeType = active.data.current?.type as string | undefined;
  const isTutorDrag = isTutorDragSource(roleGroup, activeType);
  const isStudentDrag = isStudentDragSource(roleGroup, activeType);
  let assignStudentToGroupHint = false;
  if (deps.isGroupMode) {
    assignStudentToGroupHint = await runGroupModeDragEnd(
      userData,
      isTutorDrag,
      isStudentDrag,
      over.data.current?.type as string | undefined,
      over.data.current?.tutorId as string | undefined,
      deps,
    );
  } else {
    assignStudentToGroupHint = await runClassicModeDragEnd(
      userData,
      isTutorDrag,
      isStudentDrag,
      over.id as string,
      deps,
    );
  }
  return assignStudentToGroupHint ? { assignStudentToGroupHint: true } : {};
}

/** Message when a check failed; null when valid. */
function validationBlockMessage(check: ValidateResult): string | null {
  if (check.valid) return null;
  return check.message ?? null;
}

/** Min-count and group pairing checks before submit. */
function validateParticipantsForSubmit(ctx: TutoringSubmitCtx): string | null {
  const minMessage = validationBlockMessage(
    validateMinParticipants(ctx.selectedTutors, ctx.selectedStudents, {
      isGroupMode: ctx.isGroupMode,
      studentToTutor: ctx.isGroupMode ? ctx.studentToTutor : undefined,
    }),
  );
  if (minMessage) return minMessage;
  if (!ctx.isGroupMode) return null;
  const tutorsStudentsMessage = validationBlockMessage(
    validateGroupCreateTutorsHaveStudents(ctx.selectedTutors, ctx.studentToTutor),
  );
  if (tutorsStudentsMessage) return tutorsStudentsMessage;
  return validationBlockMessage(
    validateOrphanStudentsForGroupMode(ctx.selectedStudents, ctx.studentToTutor),
  );
}

/** Re-check live availability and return a block message if anyone is busy. */
async function blockOnUnavailableParticipants(ctx: TutoringSubmitCtx): Promise<string | null> {
  if (ctx.selectedTutors.length === 0 && ctx.selectedStudents.length === 0) return null;
  ctx.setIsCheckingSubmit(true);
  try {
    if (ctx.selectedTutors.length > 0) {
      const tutorResult = await ctx.tutorAvailability.validateForSubmit();
      if (tutorResult.ok === false) {
        return buildSubmitBlockedMessage(tutorResult.unavailable);
      }
    }
    if (ctx.selectedStudents.length > 0) {
      const studentResult = await ctx.studentAvailability.validateForSubmit();
      if (studentResult.ok === false) {
        return buildStudentSubmitBlockedMessage(studentResult.unavailable);
      }
    }
  } finally {
    ctx.setIsCheckingSubmit(false);
  }
  return null;
}

/** Run participant validation on edit, or on create when the participants step is shown. */
async function maybeBlockOnParticipants(ctx: TutoringSubmitCtx): Promise<string | null> {
  const shouldCheck = ctx.mode === 'edit' || ctx.showParticipants;
  if (!shouldCheck) return null;
  const validationMessage = validateParticipantsForSubmit(ctx);
  if (validationMessage) return validationMessage;
  return blockOnUnavailableParticipants(ctx);
}

/** Group-mode DTO count and pairing checks before create. */
function validateCreateGroupDtosMessage(
  isGroupMode: boolean,
  tutors: UserWithProfile[],
  studentToTutor: Record<string, string>,
  dtos: CreateSessionDto[],
): string | null {
  if (!isGroupMode) return null;
  if (dtos.length < 1) {
    return getErrorMessage('minFacilitatorRequired');
  }
  return validationBlockMessage(validateGroupSessionDtos(tutors, studentToTutor, dtos));
}

/** Build create DTOs (or group update) and fire the mutation. */
async function submitCreateTutoring(ctx: TutoringSubmitCtx): Promise<string | null> {
  const participantBlock = await maybeBlockOnParticipants(ctx);
  if (participantBlock) return participantBlock;
  const { activityId, newActivity, newActivityType } = ctx.buildActivityData();
  const selected = ctx.getSelectedEntities();
  const baseDto = buildCreateSessionBaseDto({
    sessionData: ctx.sessionData,
    activityId,
    newActivity,
    newActivityType,
    establishmentName:
      ctx.selectedEstablishmentDetail?.name ?? selected.establishment?.name ?? null,
    programName: selected.program?.name ?? null,
    activityName: selected.activity?.label ?? null,
    roomName: selected.classroom?.name || selected.classroom?.label || null,
  });
  const dtos = buildCreateSessionDtos({
    isGroupMode: ctx.isGroupMode,
    tutors: ctx.selectedTutors,
    students: ctx.selectedStudents,
    studentToTutor: ctx.studentToTutor,
    baseDto,
  });
  const dtoBlock = validateCreateGroupDtosMessage(
    ctx.isGroupMode,
    ctx.selectedTutors,
    ctx.studentToTutor,
    dtos,
  );
  if (dtoBlock) return dtoBlock;
  if (ctx.isEditGroup) {
    ctx.updateGroupMutation.mutate(
      buildUpdateSessionGroupPayload({
        baseDto,
        sessions: mapCreateDtosToGroupChildren(
          dtos,
          ctx.tutorToChildId,
          ctx.isGroupMode,
          ctx.soleChildId,
        ),
        isRecurringSeries: ctx.isRecurringSeries,
        recurrenceUpdateScope: ctx.recurrenceUpdateScope,
      }),
    );
    return null;
  }
  ctx.createMutation.mutate({ dtos, isGroupMode: ctx.isGroupMode });
  return null;
}

/** Build the update DTO and fire the edit mutation. */
async function submitEditTutoring(ctx: TutoringSubmitCtx): Promise<string | null> {
  const participantBlock = await maybeBlockOnParticipants(ctx);
  if (participantBlock) return participantBlock;
  const { activityId, newActivity, newActivityType } = ctx.buildActivityData();
  const participantPayload = ctx.isGroupMode
    ? buildGroupModeUpdateParticipantPayload(
        ctx.selectedTutors,
        ctx.selectedStudents,
        ctx.studentToTutor,
      )
    : buildClassicModeParticipantPayload(ctx.selectedTutors, ctx.selectedStudents);
  const names = resolveEditEntityNames({
    establishmentDetailName: ctx.selectedEstablishmentDetail?.name,
    sessionData: ctx.sessionData,
    establishments: ctx.formData.establishments,
    programs: ctx.formData.programs,
    activities: ctx.formData.activities,
    classrooms: ctx.formData.classrooms,
  });
  ctx.updateMutation.mutate(
    buildUpdateSessionDto({
      sessionData: ctx.sessionData,
      activityId,
      newActivity,
      newActivityType,
      participantPayload,
      names,
      includeRecurrenceScope: !!(
        ctx.session?.recurrenceGroupId && ctx.session.mode === 'recurring'
      ),
      recurrenceUpdateScope: ctx.recurrenceUpdateScope,
    }),
  );
  return null;
}

/** Validates, builds DTOs, and fires create or update; returns a block message. */
export async function runTutoringSubmit(ctx: TutoringSubmitCtx): Promise<string | null> {
  if (ctx.mode === 'create' && (ctx.queuedJob || ctx.createPending)) return null;
  if (!ctx.validation.validateForm(ctx.sessionData)) {
    return getErrorMessage('requiredFields');
  }
  if (!ctx.validateActivityForms()) return null;
  if (ctx.mode === 'create') {
    return submitCreateTutoring(ctx);
  }
  return submitEditTutoring(ctx);
}

/** Maps an absence reason code to the tutor or student label catalog. */
export function buildAbsenceJustification(
  role: SessionParticipant['role'],
  reason?: AbsenceReason,
): string | null {
  if (!reason) return null;
  const kind = roleKind(role);
  if (kind === 'tutor' || kind === 'volunteer' || kind === 'coordinator' || kind === 'recruiter') {
    return TUTOR_ABSENCE_REASONS[reason as keyof typeof TUTOR_ABSENCE_REASONS];
  }
  return STUDENT_ABSENCE_REASONS[reason as keyof typeof STUDENT_ABSENCE_REASONS];
}

/** Attendance UI: paired groups plus unmatched facilitators/students. */
export function groupAttendanceParticipants(session: SessionWithParticipants) {
  const participants = session.participants || [];
  const pairs = session.studentTutorPairs || [];

  const pairsByTutor = new Map<string, SessionParticipant[]>();
  const matchedTutorIds = new Set<string>();
  const matchedStudentIds = new Set<string>();

  pairs.forEach((pair) => {
    matchedTutorIds.add(pair.tutorUserId);
    matchedStudentIds.add(pair.studentUserId);

    if (!pairsByTutor.has(pair.tutorUserId)) {
      pairsByTutor.set(pair.tutorUserId, []);
    }

    const studentParticipant = participants.find(
      (p) => p.userId === pair.studentUserId && p.role === 'student',
    );
    if (studentParticipant) {
      const tutorStudents = pairsByTutor.get(pair.tutorUserId);
      if (tutorStudents) {
        tutorStudents.push(studentParticipant);
      }
    }
  });

  const pairGroups: Array<{ tutor: SessionParticipant; students: SessionParticipant[] }> = [];
  pairsByTutor.forEach((students, tutorId) => {
    const tutorParticipant = participants.find(
      (p) => p.userId === tutorId && (p.role === 'tutor' || p.role === 'volunteer'),
    );
    if (tutorParticipant) {
      pairGroups.push({ tutor: tutorParticipant, students });
    }
  });

  return {
    pairGroups,
    unmatchedTutors: participants.filter(
      (p) => (p.role === 'tutor' || p.role === 'volunteer') && !matchedTutorIds.has(p.userId),
    ),
    unmatchedStudents: participants.filter(
      (p) => p.role === 'student' && !matchedStudentIds.has(p.userId),
    ),
  };
}

/** Seeds the attendance editor from existing participant rows. */
export function initialAttendanceData(
  participants: SessionWithParticipants['participants'],
): Map<string, ParticipantAttendance> {
  const initialData = new Map<string, ParticipantAttendance>();
  participants?.forEach((participant) => {
    initialData.set(participant.id, {
      participantId: participant.id,
      userId: participant.userId,
      role: participant.role,
      attendanceStatus: (participant.attendanceStatus ||
        'pending') as ParticipantAttendance['attendanceStatus'],
    });
  });
  return initialData;
}

/** Immutable Map set for one participant's attendance. */
export function setAttendanceParticipant(
  prev: Map<string, ParticipantAttendance>,
  participantId: string,
  value: ParticipantAttendance,
): Map<string, ParticipantAttendance> {
  const next = new Map(prev);
  next.set(participantId, value);
  return next;
}

/** Absent rows need a reason; reason other needs a comment. */
export function collectAttendanceValidationErrors(
  attendanceData: Map<string, ParticipantAttendance>,
): Map<string, string> {
  const errors = new Map<string, string>();
  attendanceData.forEach((value, participantId) => {
    if (value.attendanceStatus !== 'absent') return;
    if (!value.absenceReason) {
      errors.set(participantId, getErrorMessage('absenceReasonRequired'));
      return;
    }
    if (value.absenceReason === 'other' && !value.absenceComment?.trim()) {
      errors.set(participantId, getErrorMessage('specifyReason'));
    }
  });
  return errors;
}

/** Per-participant attendance PATCH bodies with justification/comment. */
export function buildAttendanceUpdateDtos(
  session: SessionWithParticipants,
  attendanceData: Map<string, ParticipantAttendance>,
): Array<{
  participantId: string;
  dto: {
    attendanceStatus: ParticipantAttendance['attendanceStatus'];
    absenceJustification: string | null;
    absenceComment: string | null;
  };
}> {
  const updates: Array<{
    participantId: string;
    dto: {
      attendanceStatus: ParticipantAttendance['attendanceStatus'];
      absenceJustification: string | null;
      absenceComment: string | null;
    };
  }> = [];
  attendanceData.forEach((attendance) => {
    const participant = session.participants?.find((p) => p.id === attendance.participantId);
    if (!participant) return;
    const isAbsent = attendance.attendanceStatus === 'absent';
    updates.push({
      participantId: attendance.participantId,
      dto: {
        attendanceStatus: attendance.attendanceStatus,
        absenceJustification: isAbsent
          ? buildAbsenceJustification(attendance.role, attendance.absenceReason)
          : null,
        absenceComment: isAbsent ? attendance.absenceComment?.trim() || null : null,
      },
    });
  });
  return updates;
}

/** Free-text detail is required when reasonCode is OTHER. */
export function isCancelReasonDetailRequired(reasonCode: string): boolean {
  return reasonCode === 'OTHER';
}

/** Clears detail unless the reason stays OTHER. */
export function nextCancelReasonDetail(reasonCode: string, currentDetail: string): string {
  if (reasonCode === 'OTHER') return currentDetail;
  return '';
}

/** Confirm payload; includes trimmed detail only for OTHER. */
export function buildCancelSessionConfirmPayload(
  reasonCode: CancelSessionReasonCode,
  reasonDetail: string,
): { reasonCode: CancelSessionReasonCode; reasonDetail?: string } {
  if (isCancelReasonDetailRequired(reasonCode)) {
    return { reasonCode, reasonDetail: reasonDetail.trim() };
  }
  return { reasonCode };
}

/** Splits create-type options into tutoring vs information-meeting tabs. */
export function groupInPersonCreateTypeOptions(options: SessionInPersonCreateTypeOption[]): {
  tutoring: SessionInPersonCreateTypeOption[];
  information: SessionInPersonCreateTypeOption[];
} {
  return {
    tutoring: options.filter((o) => o.tab === 'tutoring'),
    information: options.filter((o) => o.tab === 'information-meeting'),
  };
}

/** Extra copy explaining dates × groups or school-year end. */
export function heavyCreateSuffix(
  plannedItemCount: number,
  recurrencePreview?: { count?: number; schoolYearEndDate?: string } | null,
): string {
  if (plannedItemCount > 1) {
    return ` (${recurrencePreview?.count} dates × ${plannedItemCount} groupes de tuteurs).`;
  }
  return ` jusqu'au ${recurrencePreview?.schoolYearEndDate ?? "terme de l'année scolaire"}.`;
}

/** Which participant pane (classic / group / hint) to show. */
export function resolveTutoringParticipantVisibility(params: {
  mode: 'create' | 'edit';
  showParticipants: boolean;
  hasEstablishment: boolean;
  isGroupMode: boolean;
}): {
  showClassicParticipants: boolean;
  showGroupParticipants: boolean;
  showEstablishmentHint: boolean;
} {
  const allowed = params.mode === 'edit' || params.showParticipants;
  return {
    showClassicParticipants: allowed && params.hasEstablishment && !params.isGroupMode,
    showGroupParticipants: allowed && params.hasEstablishment && params.isGroupMode,
    showEstablishmentHint: allowed && !params.hasEstablishment,
  };
}

/** 0–100 progress for a queued multi-session create. */
export function queuedCreateProgressPercent(created: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((created / total) * 100));
}

/** Alert body from API message or computed capacity numbers. */
export function classroomCapacityAlertDescription(alert: ClassroomCapacityResult): string {
  if (alert.message) return getErrorMessage(alert.message);
  return getErrorMessage('classroomCapacityExceeded');
}

/** Create-mode form opened to edit an existing session group. */
export function isTutoringEditGroup(
  mode: 'create' | 'edit',
  editGroup?: { id?: string } | null,
): boolean {
  return mode === 'create' && !!editGroup;
}

/** Weekday numbers stored on a recurrence rule. */
export function recurrenceWeekDays(rule: Record<string, unknown> | null): number[] {
  return (rule?.weekDays as number[]) || [];
}

/** True when `day` is in the recurrence weekDays list. */
export function isRecurrenceWeekDayActive(
  rule: Record<string, unknown> | null,
  day: number,
): boolean {
  return recurrenceWeekDays(rule).includes(day);
}

/** Add or drop a weekday on the recurrence rule. */
export function toggleRecurrenceWeekDay(
  rule: Record<string, unknown> | null,
  day: number,
): Record<string, unknown> {
  const current = recurrenceWeekDays(rule);
  const next = current.includes(day)
    ? current.filter((item) => item !== day)
    : [...current, day].sort((a, b) => a - b);
  return { ...rule, weekDays: next };
}

/** Planification is only meaningful on create (filters type options). */
export function planificationTypeForMetadata(
  mode: 'create' | 'edit',
  planificationType?: PlanificationType,
): PlanificationType | undefined {
  if (mode === 'create') return planificationType;
  return undefined;
}

/** Pre-selects individual_tutoring when planification is individual. */
export function initialSessionType(planificationType?: PlanificationType): SessionType | '' {
  if (planificationType === 'individual') return 'individual_tutoring';
  return '';
}

/** Exclude current group/session from conflict checks; gated on create. */
export function resolveAvailabilityScope(params: {
  mode: 'create' | 'edit';
  showParticipants: boolean;
  editGroupId?: string;
  sessionId?: string;
}): {
  excludeSessionGroupId?: string;
  excludeSessionId?: string;
  enabled: boolean;
} {
  if (params.mode === 'create') {
    return { excludeSessionGroupId: params.editGroupId, enabled: params.showParticipants };
  }
  return { excludeSessionId: params.sessionId, enabled: true };
}

/** Recurrence preview only on fresh create with a date. */
export function isRecurrencePreviewEnabled(params: {
  mode: 'create' | 'edit';
  isEditGroup: boolean;
  isRecurringSelected: boolean;
  sessionDate: string;
}): boolean {
  return (
    params.mode === 'create' &&
    !params.isEditGroup &&
    params.isRecurringSelected &&
    Boolean(params.sessionDate)
  );
}

/** How many child sessions group mode will create (at least 1). */
export function plannedSessionItemCount(isGroupMode: boolean, tutorCount: number): number {
  if (!isGroupMode) return 1;
  return Math.max(tutorCount, 1);
}

/** Edit: wipe participants when the session type actually changes. */
export function shouldClearParticipantsOnTypeChange(
  mode: 'create' | 'edit',
  field: string,
  value: unknown,
  currentType: string,
): boolean {
  return mode === 'edit' && field === 'type' && typeof value === 'string' && value !== currentType;
}

/** Booleans become 'true'/'false' strings for the validator. */
export function fieldValueForValidation(
  value: string | string[] | boolean | Record<string, unknown> | null,
): unknown {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return value;
}

/** Banner while a background create job is running. */
export function showQueuedCreateBanner(
  mode: 'create' | 'edit',
  isEditGroup: boolean,
  hasQueuedJob: boolean,
): boolean {
  return mode === 'create' && !isEditGroup && hasQueuedJob;
}

/** Warn before submitting a large recurrence × groups create. */
export function showHeavyCreateWarning(params: {
  mode: 'create' | 'edit';
  isEditGroup: boolean;
  createPending: boolean;
  hasQueuedJob: boolean;
  isHeavyCreate: boolean;
}): boolean {
  return (
    params.mode === 'create' &&
    !params.isEditGroup &&
    !params.createPending &&
    !params.hasQueuedJob &&
    params.isHeavyCreate
  );
}

/** Title and subtitle for the tutoring create/edit form. */
export function tutoringFormHeading(params: {
  mode: 'create' | 'edit';
  isEditGroup: boolean;
  planificationType?: PlanificationType;
}): { title: string; subtitle: string } {
  if (!params.isEditGroup && params.mode !== 'edit') {
    if (params.planificationType === 'collective') {
      return {
        title: 'Créer une planification collective de séances de tutorat',
        subtitle:
          'Renseignez les informations de la planification collective de séances de tutorat',
      };
    }
    return {
      title: 'Créer une planification individuelle de séances de tutorat',
      subtitle:
        'Renseignez les informations de la planification individuelle de séances de tutorat',
    };
  }
  if (params.isEditGroup) {
    return {
      title: 'Modifier la planification de séances de tutorat',
      subtitle: 'Modifiez les informations de la planification de séances de tutorat',
    };
  }
  return {
    title: 'Modifier la séance de tutorat',
    subtitle: 'Modifiez les informations de la séance de tutorat',
  };
}

/** Close vs cancel on the form footer (queued job uses close). */
export function tutoringCloseLabel(mode: 'create' | 'edit', hasQueuedJob: boolean): string {
  if (mode === 'create' && hasQueuedJob) return 'Fermer';
  return 'Annuler';
}

/** Save on edit/group; create label otherwise. */
export function tutoringSubmitLabel(params: {
  mode: 'create' | 'edit';
  isEditGroup: boolean;
  saveLabel: string;
  createLabel: string;
}): string {
  if (params.mode === 'edit' || params.isEditGroup) return params.saveLabel;
  return params.createLabel;
}

/** Spinner while any mutation, check, or queued job is in flight. */
export function isTutoringSubmitLoading(params: {
  mode: 'create' | 'edit';
  createPending: boolean;
  updatePending: boolean;
  updateGroupPending: boolean;
  isCheckingSubmit: boolean;
  hasQueuedJob: boolean;
}): boolean {
  return (
    params.createPending ||
    params.updateGroupPending ||
    params.updatePending ||
    params.isCheckingSubmit ||
    (params.mode === 'create' && params.hasQueuedJob)
  );
}

/** Progress bar once at least one session has been created. */
export function showQueuedProgressBar(created: number, total: number): boolean {
  return total > 0 && created > 0;
}

/** Create vs edit tutor-cap copy. */
export function resolveTutorLimitMessage(params: {
  mode: 'create' | 'edit';
  sessionType: SessionType | '';
  planificationType?: PlanificationType;
  maxTutors: number | null;
  incomingName?: string;
}): string {
  if (params.mode === 'edit') {
    return tutorLimitMessageForSessionType(
      params.sessionType,
      params.maxTutors,
      params.incomingName,
    );
  }
  return tutorLimitMessageForCreate(
    params.planificationType,
    params.maxTutors,
    params.incomingName,
  );
}

/** Fetch the session only in edit mode with an id. */
export function isSessionEditQueryEnabled(mode: 'create' | 'edit', sessionId?: string): boolean {
  return mode === 'edit' && !!sessionId;
}

/** Query keys to refetch after a tutoring session mutation. */
export const TUTORING_SESSION_INVALIDATE_KEYS = [
  ['sessions'],
  ['session'],
  ['session-groups'],
  ['session-group'],
  ['reports'],
  ['report'],
  ['vsc-reports'],
  ['vsc-report'],
] as const;

/** Invalidates session, group, and report caches after save. */
export async function invalidateTutoringSessionQueries(queryClient: {
  invalidateQueries: (opts: {
    queryKey: readonly string[];
    refetchType: 'all';
  }) => Promise<unknown>;
}): Promise<void> {
  await Promise.all(
    TUTORING_SESSION_INVALIDATE_KEYS.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: 'all' }),
    ),
  );
}

/** Delay before showing a long-running bulk-create progress toast. */
const PROGRESS_TOAST_DELAY_MS = 12000;

/** Success or partial-failure copy after a bulk session create job settles. */
function settledBulkCreateMessage(job: SessionBulkJob, isGroupMode: boolean): string {
  const summary = job.result?.summary;
  const created = summary?.created ?? 0;
  const failed = summary?.failed ?? 0;
  const total = summary?.total ?? job.totalItems;
  const sessions = summary?.sessions ?? job.createdSessions;

  if (failed > 0) {
    const firstError = job.result?.results.find((r) => r.status === 'error');
    const detail = firstError?.status === 'error' ? getErrorMessage(firstError.error) : '';
    return getErrorMessage({
      code: 'bulkCreatePartialFailure',
      created: sessions,
      failed,
      detail,
    });
  }
  if (isGroupMode && created > 1) return `Séance créée avec succès (${created} groupes) !`;
  if (total === 1 && sessions <= 1) return 'Séance créée avec succès !';
  return `${sessions} séance(s) créée(s) avec succès !`;
}

/** Poll a queued bulk-create job and surface progress / settle toasts. */
export const trackSessionsBulkJob = async (
  queued: QueuedSessionsBulkJob,
  { queryClient, isGroupMode, onProgress, onSettled }: SessionsBulkTrackOptions,
): Promise<void> => {
  const expected = queued.expectedSessions ?? 0;

  // Delay the progress toast until the job proves slow; hold it on an object so the
  // setTimeout assignment is visible to the compiler (a bare local would stay null).
  const progress: { toast: ReturnType<typeof toast.loading> | null } = { toast: null };
  const revealTimer = setTimeout(() => {
    progress.toast = toast.loading(
      expected > 1 ? `Création de ${expected} séances en cours…` : 'Création des séances en cours…',
    );
  }, PROGRESS_TOAST_DELAY_MS);

  let job: SessionBulkJob | null = null;
  try {
    job = await pollSessionsBulkJob(queued.jobId, (snapshot) => {
      onProgress?.(snapshot);
      const created = snapshot.createdSessions;
      if (created <= 0) return;
      progress.toast?.update(
        expected > 0
          ? `Création des séances — ${created} / ${expected}`
          : `Création des séances — ${created} créée(s)`,
      );
    });
  } catch {
    job = null;
  }

  clearTimeout(revealTimer);
  progress.toast?.dismiss();

  if (!job) {
    toast.warning(getErrorMessage('cannotTrackBulkCreate'), { duration: 8000 });
  } else if (job.status === 'failed') {
    toast.error(getErrorMessage(job.error));
  } else if (job.status !== 'completed') {
    toast.warning(
      getErrorMessage({ code: 'bulkCreateStillRunning', createdSessions: job.createdSessions }),
      { duration: 8000 },
    );
  } else {
    const summary = job.result?.summary;
    const created = summary?.created ?? 0;
    const failed = summary?.failed ?? 0;
    const message = settledBulkCreateMessage(job, isGroupMode);

    if (failed > 0 && created === 0) toast.error(getErrorMessage(message));
    else if (failed > 0) toast.warning(getErrorMessage(message), { duration: 8000 });
    else toast.success(message);
  }

  await invalidateTutoringSessionQueries(queryClient);
  onSettled?.();
};

/** Capacity check needs a complete first slot and participants to add. */
export function shouldCheckClassroomCapacityForCreate(
  first:
    | { roomId?: string; sessionDate?: string; startTime?: string; endTime?: string }
    | undefined,
  participantsToAdd: number,
): boolean {
  return !!(
    first?.roomId &&
    first.sessionDate &&
    first.startTime &&
    first.endTime &&
    participantsToAdd > 0
  );
}

/** Central error copy from {@link getErrorMessage}. */
export function getSessionMutationErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** True for axios ECONNABORTED / ETIMEDOUT on long creates. */
export function isCreateTimeoutError(error: unknown): boolean {
  const err = error as { code?: string };
  return err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
}

/** Classifies a create mutation failure for the form. */
export function resolveCreateSessionMutationError(
  error: unknown,
): CreateSessionMutationErrorOutcome {
  if (error instanceof ClassroomCapacityExceededError) {
    return { kind: 'capacity', result: error.result };
  }
  if (isCreateTimeoutError(error)) return { kind: 'timeout' };
  return {
    kind: 'error',
    message: getErrorMessage(error),
  };
}

/** Surfaces capacity alerts, then returns the classified outcome. */
export function applyCreateSessionMutationError(
  error: unknown,
  setCapacityAlert: (result: ClassroomCapacityResult) => void,
): CreateSessionMutationErrorOutcome {
  const outcome = resolveCreateSessionMutationError(error);
  if (outcome.kind === 'capacity') {
    setCapacityAlert(outcome.result);
  }
  return outcome;
}

/** Marks one user present in the local attendance map. */
export function withParticipantPresent(
  prev: ParticipantAttendanceById,
  userId: string,
): ParticipantAttendanceById {
  return { ...prev, [userId]: { status: 'present', justification: null } };
}

/** Drops one user from the local attendance map. */
export function withoutParticipantAttendance(
  prev: ParticipantAttendanceById,
  userId: string,
): ParticipantAttendanceById {
  if (!prev[userId]) return prev;
  const next = { ...prev };
  delete next[userId];
  return next;
}

/** Clears all pairs owned by a removed facilitator. */
export function unmatchStudentsOfTutor(
  prev: Record<string, string>,
  tutorId: string,
): Record<string, string> {
  const next = { ...prev };
  for (const sid of Object.keys(next)) {
    if (next[sid] === tutorId) delete next[sid];
  }
  return next;
}

/** Removes one student's pair assignment. */
export function unmatchStudent(
  prev: Record<string, string>,
  studentId: string,
): Record<string, string> {
  if (!prev[studentId]) return prev;
  const next = { ...prev };
  delete next[studentId];
  return next;
}

/** Removes the pair only if it still points at that tutor. */
export function unmatchStudentFromTutor(
  prev: Record<string, string>,
  studentId: string,
  tutorId: string,
): Record<string, string> {
  if (prev[studentId] !== tutorId) return prev;
  const next = { ...prev };
  delete next[studentId];
  return next;
}

/** First blocking reason for a tutor/student add. */
export function resolveAddParticipantBlock(
  userId: string | undefined,
  selectedIds: string[],
  max: number | null,
): AddParticipantBlock {
  if (!userId) return 'missing-id';
  if (max !== null && selectedIds.length >= max) return 'max';
  if (selectedIds.includes(userId)) return 'already';
  return null;
}

/** Tutor-specific toast for a blocked add. */
export function addTutorBlockToast(
  block: AddParticipantBlock,
  displayName: string,
  maxMessage: string,
): AddParticipantToast | null {
  if (block === 'missing-id') {
    return { kind: 'error', text: getErrorMessage('participantMissingIdTutor') };
  }
  if (block === 'max') return { kind: 'warning', text: maxMessage };
  if (block === 'already') {
    return {
      kind: 'info',
      text: getErrorMessage({ code: 'participantAlreadyNamedTutor', name: displayName }),
    };
  }
  return null;
}

/** Student-specific toast for a blocked add. */
export function addStudentBlockToast(
  block: AddParticipantBlock,
  displayName: string,
  maxStudents: number,
): AddParticipantToast | null {
  if (block === 'missing-id') {
    return { kind: 'error', text: getErrorMessage('participantMissingIdStudent') };
  }
  if (block === 'max') {
    return {
      kind: 'error',
      text: getErrorMessage({ code: 'studentCapacityReachedWithMax', maxStudents }),
    };
  }
  if (block === 'already') {
    return {
      kind: 'info',
      text: getErrorMessage({ code: 'participantAlreadyNamedStudent', name: displayName }),
    };
  }
  return null;
}

/** Seeds currentRole so pairing treats the user as tutor or volunteer. */
export function withFacilitatorRole(user: UserWithProfile): UserWithProfile {
  return { ...user, currentRole: facilitatorRoleForSession(user) };
}

/** Seeds currentRole as student after an add. */
export function withStudentRole(user: UserWithProfile): UserWithProfile {
  return { ...user, currentRole: 'student' };
}

/** Appends a student if not already selected. */
export function ensureStudentInSelection(
  prev: UserWithProfile[],
  student: UserWithProfile,
): UserWithProfile[] {
  if (prev.some((s) => s.id === student.id)) return prev;
  return [...prev, withStudentRole(student)];
}

/** Forces individual type or clears an invalid collective type. */
export function alignSessionTypeForPlanification(
  prev: SessionFormData,
  planificationType?: PlanificationType,
): SessionFormData {
  if (planificationType === 'individual') {
    return prev.type === 'individual_tutoring' ? prev : { ...prev, type: 'individual_tutoring' };
  }
  if (planificationType === 'collective') {
    const t = prev.type;
    if (!t || COLLECTIVE_SESSION_TYPE_VALUES.includes(t)) return prev;
    return { ...prev, type: '' };
  }
  return prev;
}

/** Locks sessionFormat to in_person for this form. */
export function ensureInPersonFormat(prev: SessionFormData): SessionFormData {
  return prev.sessionFormat === 'in_person' ? prev : { ...prev, sessionFormat: 'in_person' };
}

/** Fills subjectIds once when the form has none. */
export function applyDefaultSubjectsIfEmpty(
  prev: SessionFormData,
  defaultIds: string[],
): SessionFormData | undefined {
  if (prev.subjectIds.length > 0) return undefined;
  return { ...prev, subjectIds: defaultIds };
}

/** Drops overflow students after a type change lowers the cap. */
export function trimStudentsOverMax(
  current: UserWithProfile[],
  newMax: number | null,
): { students: UserWithProfile[]; removedCount: number } {
  if (newMax === null || current.length <= newMax) return { students: current, removedCount: 0 };
  return { students: current.slice(0, newMax), removedCount: current.length - newMax };
}

/** Warning after overflow students were removed. */
export function maxStudentsTrimWarning(removedCount: number, newMax: number): string {
  return getErrorMessage({ code: 'studentsTrimmedAfterTypeChange', removedCount, newMax });
}

/** Arms then detects a type change so participants can be wiped. */
export function nextTypeResetArming(params: {
  armed: boolean;
  prevType: SessionType | '' | null;
  nextType: SessionType | '';
}): { armed: boolean; prevType: SessionType | '' | null; wipeParticipants: boolean } {
  if (!params.armed) {
    return { armed: !!params.nextType, prevType: params.nextType, wipeParticipants: false };
  }
  return {
    armed: true,
    prevType: params.nextType,
    wipeParticipants: params.prevType !== null && params.prevType !== params.nextType,
  };
}

/** Edit hydrate: form, participants, attendance, and pair map. */
export function sessionToEditPrefill(
  session: Parameters<typeof sessionToFormData>[0] & {
    participants?: Parameters<typeof extractSessionParticipants>[0];
    studentTutorPairs?: Array<{ studentUserId: string; tutorUserId: string }>;
  },
): {
  formData: SessionFormData;
  tutors: UserWithProfile[];
  students: UserWithProfile[];
  attendance: ParticipantAttendanceById;
  studentToTutor: Record<string, string>;
} {
  const extracted = session.participants
    ? extractSessionParticipants(session.participants)
    : { tutors: [], students: [], attendance: {} };
  return {
    formData: sessionToFormData(session),
    tutors: extracted.tutors,
    students: extracted.students,
    attendance: extracted.attendance,
    studentToTutor: session.studentTutorPairs?.length
      ? studentToTutorFromPairs(session.studentTutorPairs)
      : {},
  };
}

/** Type is required when planification is individual or collective. */
export function shouldValidateCreateTypeField(planificationType?: PlanificationType): boolean {
  return planificationType === 'individual' || planificationType === 'collective';
}

/** Types that support automatic student–tutor assignment. */
export function isAutoAssignableSessionType(sessionType: SessionType | ''): boolean {
  if (!sessionType) return false;
  return (
    isGroupSessionType(sessionType) ||
    sessionType === 'individual_tutoring' ||
    sessionType === 'whole_class'
  );
}

/** Why auto-assign cannot run (missing tutors, students, site, or type). */
export function autoAssignBlockMessage(params: {
  tutorCount: number;
  studentCount: number;
  establishmentId: string;
  sessionType: SessionType | '';
}): string | null {
  if (params.tutorCount === 0) return getErrorMessage('autoAssignNeedTutor');
  if (params.studentCount === 0) return getErrorMessage('autoAssignNeedStudent');
  if (!params.establishmentId) return getErrorMessage('autoAssignNeedEstablishment');
  if (!isAutoAssignableSessionType(params.sessionType)) {
    return getErrorMessage('autoAssignNeedSessionType');
  }
  return null;
}

/** Body for the auto-assign API (participants with roles). */
export function buildAutoAssignRequest(params: {
  sessionType: SessionType;
  establishmentId: string;
  subjectId?: string | null;
  activityId: string;
  tutors: UserWithProfile[];
  students: UserWithProfile[];
}) {
  return {
    sessionType: params.sessionType,
    establishmentId: params.establishmentId,
    subjectId: params.subjectId ?? null,
    activityId: params.activityId || null,
    participants: [
      ...params.tutors.map((t) => ({ userId: t.id, role: facilitatorRoleForSession(t) })),
      ...params.students.map((s) => ({ userId: s.id, role: 'student' as const })),
    ],
  };
}

/** Toast after applying proposals, warning if students remain unmatched. */
export function applyProposalResultMessage(
  assigned: number,
  studentCount: number,
): { kind: 'success' | 'warning'; text: string } {
  const unassigned = studentCount - assigned;
  if (unassigned > 0) {
    return {
      kind: 'warning',
      text: getErrorMessage({ code: 'autoAssignPartialUnassigned', unassigned }),
    };
  }
  return { kind: 'success', text: `Suggestion appliquée : ${assigned} affectation(s).` };
}

/** After add: auto-match the only tutor, or warn when several exist. */
export function resolveGroupStudentAfterAdd(
  isGroupMode: boolean,
  tutorCount: number,
): 'match-single' | 'warn-multi' | null {
  if (!isGroupMode) return null;
  if (tutorCount === 1) return 'match-single';
  if (tutorCount > 1) return 'warn-multi';
  return null;
}

/** Blocks tutor drag until the slot is complete. */
export function resolveDragStartUser(
  userData: UserWithProfile | undefined,
  isTutorDrag: boolean,
  slotComplete: boolean,
): UserWithProfile | null {
  if (isTutorDrag && !slotComplete) return null;
  return userData ?? null;
}

/** Central error copy from {@link getErrorMessage}. */
export function getSessionFormApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Hydrate the information-meeting form from GET session. */
export function informationMeetingSessionToFormState(session: {
  sessionDate?: string | Date | null;
  startTime?: string | null;
  endTime?: string | null;
  maxStudents?: number | null;
  internalNotes?: string | null;
  facilitators?: Array<{
    userId: string;
    user?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    };
  }>;
  participants?: Array<{
    role: string;
    userId: string;
    user?: {
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    };
  }>;
}): {
  sessionDate: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  internalNotes: string;
  selectedAnimators: SelectedUser[];
  selectedTutorParticipants: SelectedUser[];
} {
  return {
    sessionDate: toCalendarDateString(session.sessionDate),
    startTime: session.startTime ?? '',
    endTime: session.endTime ?? '',
    maxParticipants: session.maxStudents ?? INFORMATION_MEETING_MAX_PARTICIPANTS,
    internalNotes: session.internalNotes ?? '',
    selectedAnimators: (session.facilitators ?? []).map((facilitator) =>
      toSelectedUser({
        id: facilitator.userId,
        firstName: facilitator.user?.firstName,
        lastName: facilitator.user?.lastName,
        email: facilitator.user?.email,
      }),
    ),
    selectedTutorParticipants: (session.participants ?? [])
      .filter((participant) => isInPersonInformationMeetingAttendeeRole(participant.role))
      .map((participant) =>
        toSelectedUser({
          id: participant.userId,
          firstName: participant.user?.firstName,
          lastName: participant.user?.lastName,
          email: participant.user?.email,
        }),
      ),
  };
}

/** Terminal / cap / valid flags for the information-meeting form (today: start ≥ now). */
export function deriveInformationMeetingFormFlags(params: {
  isEdit: boolean;
  sessionStatus?: SessionStatus;
  selectedTutorCount: number;
  maxParticipants: number;
  sessionDate: string;
  startTime: string;
  endTime: string;
}): {
  isTerminal: boolean;
  participantLimitReached: boolean;
  isFormValid: boolean;
} {
  const isTerminal =
    params.isEdit && params.sessionStatus ? isTerminalStatus(params.sessionStatus) : false;
  const participantLimitReached = params.selectedTutorCount >= params.maxParticipants;
  const endAfterStart = !params.endTime || params.endTime > params.startTime;
  const minStart = getMinStartTimeForSessionDate(params.sessionDate);
  const startNotInPast = !minStart || !params.startTime || params.startTime >= minStart;
  return {
    isTerminal,
    participantLimitReached,
    isFormValid:
      !!params.sessionDate && !!params.startTime && endAfterStart && startNotInPast && !isTerminal,
  };
}

/** Create DTO for an in-person information meeting. */
export function buildInformationMeetingCreateDto(params: {
  sessionDate: string;
  startTime: string;
  endTime: string;
  maxParticipants: number;
  internalNotes: string;
  volunteerIds: string[];
  animatorIds: string[];
}): CreateSessionDto {
  return {
    type: 'information_meeting_in_person',
    sessionFormat: 'in_person',
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime || '',
    mode: 'single',
    recurrenceType: 'once',
    maxStudents: params.maxParticipants,
    internalNotes: params.internalNotes || undefined,
    volunteerIds: params.volunteerIds,
    animatorIds: params.animatorIds,
  };
}

/** Update DTO for an in-person information meeting. */
export function buildInformationMeetingUpdateDto(params: {
  sessionDate: string;
  startTime: string;
  endTime: string;
  animatorIds: string[];
  maxParticipants: number;
  internalNotes: string;
}): UpdateSessionDto {
  return {
    sessionDate: params.sessionDate,
    startTime: params.startTime,
    endTime: params.endTime || undefined,
    animatorIds: params.animatorIds,
    maxStudents: params.maxParticipants,
    internalNotes: params.internalNotes || undefined,
    establishmentId: null,
  };
}

/** Form picker uses `volunteer`; tutor-form join uses `tutor`. */
function isInPersonInformationMeetingAttendeeRole(role: string): boolean {
  return role === 'volunteer' || roleKind(role) === 'tutor';
}

/** Add/remove RI attendees to match the current picker (`volunteer` + `tutor` rows). */
export async function syncInformationMeetingVolunteers(
  sessionId: string,
  existingParticipants: Array<{ id: string; userId: string; role: string }> | undefined,
  selectedTutorParticipants: Array<{ id: string }>,
): Promise<void> {
  const existingAttendeeIds = new Set(
    existingParticipants
      ?.filter((p) => isInPersonInformationMeetingAttendeeRole(p.role))
      .map((p) => p.userId) ?? [],
  );
  const newAttendeeIds = new Set(selectedTutorParticipants.map((t) => t.id));
  const toAdd = selectedTutorParticipants.filter((t) => !existingAttendeeIds.has(t.id));
  const toRemove =
    existingParticipants?.filter(
      (p) => isInPersonInformationMeetingAttendeeRole(p.role) && !newAttendeeIds.has(p.userId),
    ) ?? [];
  await Promise.allSettled([
    ...toAdd.map((t) =>
      addSessionParticipant(sessionId, {
        userId: t.id,
        role: 'volunteer',
        attendanceStatus: 'pending',
      }),
    ),
    ...toRemove.map((p) => deleteSessionParticipant(sessionId, p.id)),
  ]);
}

/** Confirm-cancel copy for a single session or a group. */
export function cancelSessionDescription(
  isGroupCancel: boolean,
  childCount: number | undefined,
  itemName: string | undefined,
): string {
  if (isGroupCancel && childCount != null) {
    const plural = childCount > 1 ? 's' : '';
    return `${childCount} séance${plural} seront annulées. Cette action est irréversible.`;
  }
  if (isGroupCancel) {
    return 'Toutes les séances du groupe seront annulées. Cette action est irréversible.';
  }
  if (itemName) {
    return `Annuler « ${itemName} » ? Cette action est irréversible.`;
  }
  return 'Cette action est irréversible.';
}
